function _classCallCheck(a, n) { if (!(a instanceof n)) throw new TypeError("Cannot call a class as a function"); }
function _defineProperties(e, r) { for (var t = 0; t < r.length; t++) { var o = r[t]; o.enumerable = o.enumerable || !1, o.configurable = !0, "value" in o && (o.writable = !0), Object.defineProperty(e, _toPropertyKey(o.key), o); } }
function _createClass(e, r, t) { return r && _defineProperties(e.prototype, r), t && _defineProperties(e, t), Object.defineProperty(e, "prototype", { writable: !1 }), e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// ../internal/src/providers/websocket-provider/websocket-provider.tsx
import React from "react";
import { useSignalPublisher, useSignalSubscriber } from "@shared-ui/core-signal-manager/context";
import { VacChatMessageReceivedSignal, VacConnectWebsocketSignal } from "@shared-ui/retail-signals-virtual-agent";

// ../internal/src/providers/event/event-context-types.tsx
var globalEventId = "VAC_GLOBAL_EVENT";

// ../internal/src/providers/websocket-provider/constants.ts
var DEFAULT = {
  unsubscribe: () => null,
  disconnect: () => null
};
var DEFAULT_LIFECYCLE_CALLBACKS = {
  onConnected: () => null,
  onDisconnected: () => null
};

// ../internal/src/providers/websocket-provider/websocket-provider.tsx
import * as Ably from "ably";
var WebsocketContext = React.createContext(DEFAULT);
var WebsocketProvider = props => {
  var _a;
  const {
    children,
    client,
    subscriptionId,
    lifeCycleCallbacks,
    lifeCycleCallbackDependencies
  } = props;
  const ablyRealtimeRef = React.useRef();
  const {
    onConnected,
    onDisconnected
  } = lifeCycleCallbacks;
  const {
    publish
  } = useSignalPublisher();
  if (!ablyRealtimeRef.current && client.token && subscriptionId) {
    const ablyRealtime = new Ably.Realtime.Promise({
      ...client
    });
    const channel = ablyRealtime.channels.get(subscriptionId);
    channel.subscribe(message => {
      publish(new VacChatMessageReceivedSignal("VAC_CHAT_MESSAGE_RECEIVED" /* VAC_ChatMsgReceived */, {
        message
      }));
    });
    ablyRealtimeRef.current = {
      channel,
      ablyRealtime
    };
  }
  const unsubscribe = React.useCallback(() => {
    var _a2;
    return (_a2 = ablyRealtimeRef.current) == null ? void 0 : _a2.channel.unsubscribe();
  }, [subscriptionId]);
  const disconnect = React.useCallback(() => {
    var _a2;
    return (_a2 = ablyRealtimeRef.current) == null ? void 0 : _a2.ablyRealtime.connection.off();
  }, [client.clientId]);
  const value = React.useMemo(() => {
    var _a2;
    return {
      unsubscribe,
      disconnect,
      ablyRealtime: (_a2 = ablyRealtimeRef.current) == null ? void 0 : _a2.ablyRealtime
    };
  }, [unsubscribe, disconnect, (_a = ablyRealtimeRef.current) == null ? void 0 : _a.ablyRealtime]);
  React.useEffect(() => {
    return () => {
      unsubscribe();
    };
  }, []);
  React.useEffect(() => {
    var _a2, _b;
    disconnect();
    (_a2 = ablyRealtimeRef.current) == null ? void 0 : _a2.ablyRealtime.connection.on("connected", () => onConnected());
    (_b = ablyRealtimeRef.current) == null ? void 0 : _b.ablyRealtime.connection.on("disconnected", () => onDisconnected());
    return () => disconnect();
  }, [lifeCycleCallbackDependencies]);
  return /* @__PURE__ */React.createElement(WebsocketContext.Provider, {
    value
  }, children);
};
WebsocketProvider.displayName = "WebsocketProvider";
var useWebsocket = () => {
  const websocketContext = React.useContext(WebsocketContext);
  return websocketContext;
};
var withWebsocket = WrappedComponent => {
  const Wrapper = props => {
    const [ablyClient, setAblyClient] = React.useState({
      clientId: "",
      token: "",
      environment: "",
      fallbackHosts: []
    });
    const [ablySubscriptionId, setAblySubscriptionId] = React.useState("");
    const [lifeCycleCbs, setLifeCycleCbs] = React.useState(DEFAULT_LIFECYCLE_CALLBACKS);
    const [lifeCycleCallbackDeps, setLifeCycleCallbackDeps] = React.useState([]);
    useSignalSubscriber("VAC_CONNECT_WEBSOCKET" /* VAC_ConnectWebsocket */, VacConnectWebsocketSignal, {
      onSuccess: ({
        payload: {
          client,
          subscriptionId,
          lifeCycleCallbacks,
          lifeCycleCallbackDependencies
        }
      }) => {
        if (!lifeCycleCallbacks) {
          throw Error("onConnected and onDisconnected callbacks are required.");
        }
        if (!Array.isArray(lifeCycleCallbackDependencies)) {
          throw Error("lifeCycleCallbackDependencies must be an array");
        }
        const {
          onConnected,
          onDisconnected
        } = lifeCycleCallbacks;
        if (typeof onConnected !== "function" || typeof onDisconnected !== "function") {
          throw Error("onConnected and onDisconnected should be function");
        }
        setAblyClient(client);
        setAblySubscriptionId(subscriptionId);
        setLifeCycleCbs(lifeCycleCallbacks);
        setLifeCycleCallbackDeps(lifeCycleCallbackDependencies);
      }
    });
    return /* @__PURE__ */React.createElement(WebsocketProvider, {
      client: ablyClient,
      subscriptionId: ablySubscriptionId,
      lifeCycleCallbacks: lifeCycleCbs,
      lifeCycleCallbackDependencies: lifeCycleCallbackDeps
    }, /* @__PURE__ */React.createElement(WrappedComponent, {
      ...props
    }));
  };
  Wrapper.displayName = "withWebsocket";
  return Wrapper;
};

// ../internal/src/store/vac-state-provider.tsx
import React2 from "react";
import { buildStateProvider } from "@shared-ui/provided-state-context";

// ../internal/src/store/chat-instances-state/constants.ts
var ChatInstancesStateEvent = /* @__PURE__ */(ChatInstancesStateEvent2 => {
  ChatInstancesStateEvent2["SET_CHAT_CONFIG"] = "SET_CHAT_CONFIG";
  ChatInstancesStateEvent2["SET_EMBEDDED"] = "SET_EMBEDDED";
  ChatInstancesStateEvent2["SET_ACTIVE_CONVERSATION_ID"] = "SET_ACTIVE_CONVERSATION_ID";
  ChatInstancesStateEvent2["SET_INITIALIZATION_LOADING"] = "SET_INITIALIZATION_LOADING";
  ChatInstancesStateEvent2["SET_SETTINGS_MENU_STATE"] = "SET_SETTINGS_MENU_STATE";
  return ChatInstancesStateEvent2;
})(ChatInstancesStateEvent || {});

// ../internal/src/store/chat-instances-state/reducers.ts
import isEqual from "lodash.isequal";
var initChatInstance = () => ({
  chatConfig: {
    partnerGuid: "",
    channelOriginId: "",
    enableHistory: false,
    notificationConfig: {
      enableSound: false,
      audioUrl: ""
    },
    presenceConfig: {
      mode: "NONE" /* NONE */,
      heartbeatIntervalInMillis: 0
    },
    participantTypesForEventMarkers: [],
    showParticipantEventMarker: false
  },
  activeConversationId: null,
  embedded: false,
  initializationLoading: true,
  settingMenuItemState: {
    toggleSound: false
  }
});
var setChatConfig = (state, payload) => {
  var _a;
  const {
    vacId,
    chatConfig
  } = payload;
  if (isEqual((_a = state[vacId]) == null ? void 0 : _a.chatConfig, chatConfig)) {
    return state;
  }
  const chatInstance = state[vacId] || initChatInstance();
  return {
    ...state,
    [vacId]: {
      ...chatInstance,
      chatConfig: {
        ...chatInstance.chatConfig,
        ...chatConfig
      }
    }
  };
};
var setEmbedded = (state, payload) => {
  var _a;
  const {
    vacId,
    embedded
  } = payload;
  if (((_a = state[vacId]) == null ? void 0 : _a.embedded) === embedded) {
    return state;
  }
  const chatInstance = state[vacId] || initChatInstance();
  return {
    ...state,
    [vacId]: {
      ...chatInstance,
      embedded
    }
  };
};
var setActiveConversationId = (state, payload) => {
  var _a;
  const {
    vacId,
    activeConversationId
  } = payload;
  if (((_a = state[vacId]) == null ? void 0 : _a.activeConversationId) === activeConversationId) {
    return state;
  }
  const chatInstance = state[vacId] || initChatInstance();
  return {
    ...state,
    [vacId]: {
      ...chatInstance,
      activeConversationId
    }
  };
};
var setInitializationLoading = (state, payload) => {
  var _a;
  const {
    vacId,
    initializationLoading
  } = payload;
  if (((_a = state[vacId]) == null ? void 0 : _a.initializationLoading) === initializationLoading) {
    return state;
  }
  const chatInstance = state[vacId] || initChatInstance();
  return {
    ...state,
    [vacId]: {
      ...chatInstance,
      initializationLoading
    }
  };
};
var setSettingMenuItemState = (state, payload) => {
  var _a, _b;
  const {
    vacId,
    menuItemId,
    menuItemState
  } = payload;
  if (isEqual((_b = (_a = state[vacId]) == null ? void 0 : _a.settingMenuItemState) == null ? void 0 : _b[menuItemId], menuItemState)) {
    return state;
  }
  const chatInstance = state[vacId] || initChatInstance();
  return {
    ...state,
    [vacId]: {
      ...chatInstance,
      settingMenuItemState: {
        ...chatInstance.settingMenuItemState,
        [menuItemId]: menuItemState
      }
    }
  };
};

// ../internal/src/store/chat-instances-state/chat-instances-state.ts
var initChatInstancesState = {};
var ChatInstancesState = /*#__PURE__*/function () {
  function ChatInstancesState() {
    _classCallCheck(this, ChatInstancesState);
    this.stateFieldName = "chatInstancesState";
    this.data = initChatInstancesState;
    this.events = Object.values(ChatInstancesStateEvent);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return _createClass(ChatInstancesState, [{
    key: "update",
    value: function update(event, currentState, payload) {
      var _a;
      if (!((_a = payload.data) == null ? void 0 : _a.vacId)) {
        return currentState;
      }
      switch (event) {
        case "SET_CHAT_CONFIG" /* SET_CHAT_CONFIG */:
          return setChatConfig(currentState, payload.data);
        case "SET_EMBEDDED" /* SET_EMBEDDED */:
          return setEmbedded(currentState, payload.data);
        case "SET_ACTIVE_CONVERSATION_ID" /* SET_ACTIVE_CONVERSATION_ID */:
          return setActiveConversationId(currentState, payload.data);
        case "SET_INITIALIZATION_LOADING" /* SET_INITIALIZATION_LOADING */:
          return setInitializationLoading(currentState, payload.data);
        case "SET_SETTINGS_MENU_STATE" /* SET_SETTINGS_MENU_STATE */:
          return setSettingMenuItemState(currentState, payload.data);
        default:
          return currentState;
      }
    }
  }]);
}();

// ../internal/src/store/conversations/constants.tsx
var ConversationsStateEvent = /* @__PURE__ */(ConversationsStateEvent2 => {
  ConversationsStateEvent2["UPDATE_CONVERSATION"] = "UPDATE_CONVERSATION";
  ConversationsStateEvent2["SET_FOOTER_STATE"] = "SET_FOOTER_STATE";
  return ConversationsStateEvent2;
})(ConversationsStateEvent || {});

// ../internal/src/store/conversations/reducers.tsx
import isEqual2 from "lodash.isequal";
var initConversation = conversationId => ({
  conversationId,
  participantId: null,
  type: null,
  state: "ACTIVE" /* ACTIVE */,
  chatFooterState: {
    chatInputStatus: null,
    interactionMode: null,
    chatInputEnable: false
  }
});
var updateConversationsById = (state, conversationId, conversation) => {
  if (!isEqual2(state.conversationsById[conversationId], conversation)) {
    return {
      ...state,
      conversationsById: {
        ...state.conversationsById,
        [conversationId]: conversation
      }
    };
  }
  return state;
};
var updateConversation = (state, payload) => {
  if (payload == null ? void 0 : payload.conversationId) {
    const conversation = state.conversationsById[payload.conversationId] || initConversation(payload.conversationId);
    const updatedConversation = {
      ...conversation,
      participantId: payload.participantId || conversation.participantId,
      type: payload.type || conversation.type,
      state: payload.state || conversation.state
    };
    if (payload == null ? void 0 : payload.conversationHistoryAttributes) {
      updatedConversation.conversationHistoryAttributes = payload.conversationHistoryAttributes;
    }
    return updateConversationsById(state, payload.conversationId, updatedConversation);
  }
  return state;
};
function handleInputEnable(chatInputStatus, interactionMode) {
  if (chatInputStatus) {
    return chatInputStatus === "ENABLE" /* ENABLE */;
  }
  if (interactionMode) {
    return interactionMode === "FREE" /* FREE */;
  }
  return true;
}
var updateChatFooterState = (state, payload) => {
  var _a;
  const {
    conversationId,
    chatFooterState
  } = payload;
  const conversation = state.conversationsById[conversationId] || initConversation(conversationId);
  let chatInputEnable = handleInputEnable(chatFooterState.chatInputStatus, chatFooterState.interactionMode);
  let updatedConversation = {
    ...conversation,
    chatFooterState: {
      chatInputStatus: chatFooterState.chatInputStatus,
      interactionMode: chatFooterState.interactionMode,
      chatInputEnable
    }
  };
  if (((_a = conversation.chatFooterState) == null ? void 0 : _a.interactionMode) !== null && chatFooterState.interactionMode == null) {
    const {
      chatInputStatus
    } = chatFooterState;
    const {
      interactionMode
    } = conversation.chatFooterState;
    chatInputEnable = handleInputEnable(chatInputStatus, interactionMode);
    updatedConversation = {
      ...conversation,
      chatFooterState: {
        chatInputStatus,
        interactionMode,
        chatInputEnable
      }
    };
  }
  return updateConversationsById(state, conversationId, updatedConversation);
};

// ../internal/src/store/conversations/conversations-state.tsx
var initConversationsState = {
  conversationsById: {}
};
var ConversationsState = /*#__PURE__*/function () {
  function ConversationsState() {
    _classCallCheck(this, ConversationsState);
    this.data = initConversationsState;
    this.stateFieldName = "conversationsState";
    this.events = Object.values(ConversationsStateEvent);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return _createClass(ConversationsState, [{
    key: "update",
    value: function update(event, state, payload) {
      switch (event) {
        case "UPDATE_CONVERSATION" /* UPDATE_CONVERSATION */:
          return updateConversation(state, payload.data);
        case "SET_FOOTER_STATE" /* SET_FOOTER_STATE */:
          return updateChatFooterState(state, payload.data);
        default:
          return state;
      }
    }
  }]);
}();

// ../internal/src/store/errors/constants.tsx
var ErrorsStateEvent = /* @__PURE__ */(ErrorsStateEvent2 => {
  ErrorsStateEvent2["SET_ERROR_BANNER"] = "SET_ERROR_BANNER";
  ErrorsStateEvent2["SET_FULL_PAGE_VIEW"] = "SET_FULL_PAGE_VIEW";
  ErrorsStateEvent2["CLEAR_ERRORS"] = "CLEAR_ERRORS";
  ErrorsStateEvent2["SET_ORCHESTRATOR_ERROR"] = "SET_ORCHESTRATOR_ERROR";
  ErrorsStateEvent2["SET_OFFLINE_ERROR_STRINGS"] = "SET_OFFLINE_ERROR_STRINGS";
  ErrorsStateEvent2["SET_HISTORY_MESSAGES_FETCHING_ERROR"] = "SET_HISTORY_MESSAGES_FETCHING_ERROR";
  return ErrorsStateEvent2;
})(ErrorsStateEvent || {});
var ORCHESTRATOR = "orchestrator";
var defaultErrorBanner = {
  header: "Something went wrong",
  text: "Please refresh the page or try again later",
  retryText: null
};
var defaultErrorFullPageView = {
  ...defaultErrorBanner,
  __typename: "VirtualAgentControlErrorFullPageView",
  icon: {
    __typename: "Icon",
    id: "error",
    description: "error icon",
    title: "error icon"
  }
};
var connectionLostErrorFullPageView = {
  __typename: "VirtualAgentControlErrorFullPageView",
  header: "We're having trouble loading",
  text: "Please refresh the page or try again later",
  retryText: null,
  icon: {
    __typename: "Icon",
    id: "error",
    description: "error icon",
    title: "error icon"
  }
};
var connectionLostErrorBanner = {
  __typename: "VirtualAgentControlErrorBanner",
  header: "We lost connection",
  text: "Try to reconnect or refresh the page later",
  retryText: null
};
var fetchCredentialsFailedErrorBanner = {
  __typename: "VirtualAgentControlErrorBanner",
  text: "Please refresh the page to continue",
  header: "Your session expired",
  retryText: null
};
var fetchCredentialsFailedErrorFullPageView = {
  ...fetchCredentialsFailedErrorBanner,
  __typename: "VirtualAgentControlErrorFullPageView",
  icon: {
    __typename: "Icon",
    description: "error",
    id: "error",
    title: "error"
  }
};
var getLocalizedErrors = (errorStrings, errorName) => {
  switch (errorName) {
    case "connectionLost":
      return {
        fullPageView: {
          ...connectionLostErrorFullPageView,
          header: errorStrings == null ? void 0 : errorStrings.havingTroubleLoading,
          text: errorStrings == null ? void 0 : errorStrings.refreshOrTryLater
        },
        banner: {
          ...connectionLostErrorBanner,
          header: errorStrings == null ? void 0 : errorStrings.lostConnection,
          text: errorStrings == null ? void 0 : errorStrings.refreshOrReconnect
        }
      };
    case "fetchCredentialsFailed":
      return {
        fullPageView: {
          ...fetchCredentialsFailedErrorFullPageView,
          header: errorStrings == null ? void 0 : errorStrings.sessionExpired,
          text: errorStrings == null ? void 0 : errorStrings.refreshToContinue
        },
        banner: {
          ...fetchCredentialsFailedErrorBanner,
          header: errorStrings == null ? void 0 : errorStrings.sessionExpired,
          text: errorStrings == null ? void 0 : errorStrings.refreshToContinue
        }
      };
    default:
      return {
        fullPageView: {
          ...defaultErrorFullPageView,
          header: errorStrings == null ? void 0 : errorStrings.somethingWentWrong,
          text: errorStrings == null ? void 0 : errorStrings.refreshOrTryLater
        },
        banner: {
          ...defaultErrorBanner,
          header: errorStrings == null ? void 0 : errorStrings.somethingWentWrong,
          text: errorStrings == null ? void 0 : errorStrings.refreshOrTryLater
        }
      };
  }
};
var orchestratorErrors = {
  default: {
    fullPageView: defaultErrorFullPageView,
    banner: defaultErrorBanner
  },
  connectionLost: {
    fullPageView: connectionLostErrorFullPageView,
    banner: connectionLostErrorBanner
  },
  fetchCredentialsFailed: {
    fullPageView: fetchCredentialsFailedErrorFullPageView,
    banner: fetchCredentialsFailedErrorBanner
  }
};

// ../internal/src/store/errors/reducers.tsx
import isEqual3 from "lodash.isequal";
var updateChatError = (state, source, error) => {
  if (!isEqual3(state[source], error)) {
    return {
      ...state,
      [source]: error
    };
  }
  return state;
};
var setErrorBanner = (state, data) => {
  const {
    source,
    errorBanner
  } = data || {};
  if (source && errorBanner) {
    const updatedError = {
      active: true,
      errorComponent: {
        errorBanner
      }
    };
    return updateChatError(state, source, updatedError);
  }
  return state;
};
var setFullPageView = (state, data) => {
  const {
    source,
    fullPageView
  } = data || {};
  if (source && fullPageView) {
    const updatedError = {
      active: true,
      errorComponent: {
        fullPageView
      }
    };
    return updateChatError(state, source, updatedError);
  }
  return state;
};
var clearErrors = (state, data) => {
  const {
    source
  } = data || {};
  if (source) {
    const updatedError = source === ORCHESTRATOR ? {
      ...state[ORCHESTRATOR],
      active: false,
      errorComponent: {}
    } : {
      active: false,
      errorComponent: {}
    };
    return updateChatError(state, source, updatedError);
  }
  return state;
};
var setOrchestratorError = (state, data) => {
  const {
    errorName,
    errorBanner,
    fullPageView,
    errorStrings
  } = data || {};
  let updatedError;
  const currentErrorState = state[ORCHESTRATOR];
  if (errorName && orchestratorErrors[errorName]) {
    const localizedErrorContent = errorStrings ? getLocalizedErrors(errorStrings, errorName) : null;
    if (localizedErrorContent) {
      updatedError = {
        ...currentErrorState,
        active: true,
        errorComponent: {
          errorBanner: localizedErrorContent.banner,
          fullPageView: localizedErrorContent.fullPageView
        }
      };
    } else {
      updatedError = {
        ...currentErrorState,
        active: true,
        errorComponent: {
          errorBanner: orchestratorErrors[errorName].banner,
          fullPageView: orchestratorErrors[errorName].fullPageView
        }
      };
    }
  } else if (errorBanner || fullPageView) {
    updatedError = {
      ...currentErrorState,
      active: true,
      errorComponent: {
        errorBanner,
        fullPageView
      }
    };
  }
  return updateChatError(state, ORCHESTRATOR, updatedError);
};
var setOfflineErrorStrings = (state, data) => {
  const {
    offlineLocalizedErrorStrings
  } = data || {};
  const updatedError = {
    active: false,
    errorComponent: {},
    offlineLocalizedErrorStrings
  };
  return updateChatError(state, ORCHESTRATOR, updatedError);
};
var setHistoryMessagesFetchingError = (state, data) => {
  const {
    source,
    chatHistoryFetchingError
  } = data;
  if (source) {
    const updatedError = {
      active: true,
      errorComponent: {
        chatHistoryFetchingError
      }
    };
    return updateChatError(state, source, updatedError);
  }
  return state;
};

// ../internal/src/store/errors/errors-state.tsx
var initErrorsState = {};
var ErrorsState = /*#__PURE__*/function () {
  function ErrorsState() {
    _classCallCheck(this, ErrorsState);
    this.data = initErrorsState;
    this.stateFieldName = "errorsState";
    this.events = Object.values(ErrorsStateEvent);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return _createClass(ErrorsState, [{
    key: "update",
    value: function update(event, state, payload) {
      switch (event) {
        case "SET_ERROR_BANNER" /* SET_ERROR_BANNER */:
          return setErrorBanner(state, payload.data);
        case "SET_FULL_PAGE_VIEW" /* SET_FULL_PAGE_VIEW */:
          return setFullPageView(state, payload.data);
        case "CLEAR_ERRORS" /* CLEAR_ERRORS */:
          return clearErrors(state, payload.data);
        case "SET_ORCHESTRATOR_ERROR" /* SET_ORCHESTRATOR_ERROR */:
          return setOrchestratorError(state, payload.data);
        case "SET_OFFLINE_ERROR_STRINGS" /* SET_OFFLINE_ERROR_STRINGS */:
          return setOfflineErrorStrings(state, payload.data);
        case "SET_HISTORY_MESSAGES_FETCHING_ERROR" /* SET_HISTORY_MESSAGES_FETCHING_ERROR */:
          return setHistoryMessagesFetchingError(state, payload.data);
        default:
          return state;
      }
    }
  }]);
}();

// ../internal/src/store/loading/constants.tsx
var LoadingStateEvent = /* @__PURE__ */(LoadingStateEvent2 => {
  LoadingStateEvent2["SET_LOADING_DIALOG"] = "SET_LOADING_DIALOG";
  LoadingStateEvent2["CLEAR_LOADING"] = "CLEAR_LOADING";
  return LoadingStateEvent2;
})(LoadingStateEvent || {});

// ../internal/src/store/loading/reducers.tsx
import isEqual4 from "lodash.isequal";
var updateChatLoading = (state, source, loading) => {
  if (!isEqual4(state[source], loading)) {
    return {
      ...state,
      [source]: loading
    };
  }
  return state;
};
var setLoadingDialog = (state, data) => {
  const {
    source,
    loadingDialog
  } = data || {};
  if (source && loadingDialog) {
    const updatedLoading = {
      active: true,
      loadingComponent: {
        loadingDialog
      }
    };
    return updateChatLoading(state, source, updatedLoading);
  }
  return state;
};
var clearLoading = (state, data) => {
  const {
    source
  } = data || {};
  if (source) {
    const updatedLoading = {
      active: false,
      loadingComponent: {}
    };
    return updateChatLoading(state, source, updatedLoading);
  }
  return state;
};

// ../internal/src/store/loading/loading-state.tsx
var initLoadingState = {};
var LoadingState = /*#__PURE__*/function () {
  function LoadingState() {
    _classCallCheck(this, LoadingState);
    this.data = initLoadingState;
    this.stateFieldName = "loadingState";
    this.events = Object.values(LoadingStateEvent);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return _createClass(LoadingState, [{
    key: "update",
    value: function update(event, state, payload) {
      switch (event) {
        case "SET_LOADING_DIALOG" /* SET_LOADING_DIALOG */:
          return setLoadingDialog(state, payload.data);
        case "CLEAR_LOADING" /* CLEAR_LOADING */:
          return clearLoading(state, payload.data);
        default:
          return state;
      }
    }
  }]);
}();

// ../internal/src/store/messages/constants.tsx
var MessagesStateEvent = /* @__PURE__ */(MessagesStateEvent2 => {
  MessagesStateEvent2["ADD_MESSAGE"] = "ADD_MESSAGE";
  MessagesStateEvent2["CLEAR_MESSAGES"] = "CLEAR_MESSAGES";
  MessagesStateEvent2["SET_QUICK_REPLIES"] = "SET_QUICK_REPLIES";
  MessagesStateEvent2["UPDATE_OUTBOUND_MESSAGE"] = "UPDATE_OUTBOUND_MESSAGE";
  MessagesStateEvent2["UPLOAD_ATTACHMENT"] = "UPLOAD_ATTACHMENT";
  MessagesStateEvent2["UPLOAD_ATTACHMENT_SUCCESS"] = "UPLOAD_ATTACHMENT_SUCCESS";
  MessagesStateEvent2["UPLOAD_ATTACHMENT_FAILURE"] = "UPLOAD_ATTACHMENT_FAILURE";
  MessagesStateEvent2["UPDATE_OUTBOUND_MESSAGE_ELEMENT"] = "UPDATE_OUTBOUND_MESSAGE_ELEMENT";
  MessagesStateEvent2["UPDATE_ATTACHMENT_STATUS_MAP"] = "UPDATE_ATTACHMENT_STATUS_MAP";
  MessagesStateEvent2["SET_UNREAD_MESSAGE_COUNT"] = "SET_UNREAD_MESSAGE_COUNT";
  MessagesStateEvent2["CLEAR_UNREAD_MESSAGE_COUNT"] = "CLEAR_UNREAD_MESSAGE_COUNT";
  MessagesStateEvent2["ADD_SEPERATOR_MESSAGE"] = "ADD_SEPERATOR_MESSAGE";
  MessagesStateEvent2["SET_LAST_MESSAGE_TIMESTAMP"] = "SET_LAST_MESSAGE_TIMESTAMP";
  MessagesStateEvent2["SET_ANNOUNCE_MESSAGE_ID"] = "SET_ANNOUNCE_MESSAGE_ID";
  MessagesStateEvent2["FETCH_HISTORY_MESSAGES_STATUS"] = "FETCH_HISTORY_MESSAGES_STATUS";
  MessagesStateEvent2["ADD_HISTORY_MESSAGES"] = "ADD_HISTORY_MESSAGES";
  MessagesStateEvent2["ADD_LOADER_MESSAGE"] = "ADD_LOADER_MESSAGE";
  MessagesStateEvent2["REMOVE_LOADER_MESSAGE"] = "REMOVE_LOADER_MESSAGE";
  MessagesStateEvent2["UPDATE_MESSAGE_LOADER_VISIBILITY"] = "UPDATE_MESSAGE_LOADER_VISIBILITY";
  MessagesStateEvent2["RELOCATE_MESSAGES_TO_NEW_CONV"] = "RELOCATE_MESSAGES_TO_NEW_CONV";
  MessagesStateEvent2["UPDATE_PARTICIPANT_MARKERS"] = "UPDATE_PARTICIPANT_MARKERS";
  MessagesStateEvent2["SET_IBM_FLOW"] = "SET_IBM_FLOW";
  return MessagesStateEvent2;
})(MessagesStateEvent || {});

// ../internal/src/helpers/constants.tsx
var MessageTypes = /* @__PURE__ */(MessageTypes2 => {
  MessageTypes2["TEXT"] = "TEXT";
  MessageTypes2["ACTIONS"] = "ACTIONS";
  MessageTypes2["CAROUSEL"] = "CAROUSEL";
  MessageTypes2["DECLARATIVE_CARDS"] = "DECLARATIVE_CARDS";
  MessageTypes2["FILE"] = "FILE";
  MessageTypes2["POSTBACK"] = "POSTBACK";
  return MessageTypes2;
})(MessageTypes || {});
var AttachmentOperationTypes = {
  DOWNLOAD: "download",
  UPLOAD: "upload"
};
var LocalizationContentKeys = {
  ATTACHMENT: "attachment",
  DIALOG: "dialog",
  ACTIVITY_INDICATOR: "activityIndicator",
  ERROR: "error",
  MESSAGE: "message",
  PARTICIPANT: "participant",
  LOADING: "loading"
};

// ../internal/src/models/message-creator.tsx
function createMessageSeperator(primary, timeStamp, participantId) {
  return {
    primary,
    timeStamp,
    meta: {
      participantId,
      __typename: "VirtualAgentControlSeparatorMetadata"
    },
    __typename: "VirtualAgentControlMessageSeparator"
  };
}

// ../internal/src/store/messages/helpers.ts
var isGroupableInboundMessage = __typename => __typename === "VirtualAgentControlInboundTextMessage" || __typename === "VirtualAgentControlInboundDynamicCardMessage" || __typename === "VirtualAgentControlInboundCardMessage" || __typename === "VirtualAgentControlInboundFileMessage";
var createInboundGroup = message => {
  var _a, _b, _c;
  return {
    __typename: "VirtualAgentControlInboundMessageGroup",
    messages: [message],
    eyebrow: (_a = message == null ? void 0 : message.eyebrow) != null ? _a : null,
    timeStamp: (_c = (_b = message == null ? void 0 : message.metadata) == null ? void 0 : _b.timeStamp) != null ? _c : null
  };
};

// ../internal/src/store/messages/reducers.tsx
var initConversationMessages = () => ({
  messages: [],
  quickReplies: [],
  messageId: null,
  unreadMessageCount: 0,
  lastReceivedMessageTimeStamp: null,
  announceMessageId: null,
  messageIdSet: /* @__PURE__ */new Set(),
  fetchHistoryMessagesStatus: null
});
var getConversationMessages = (state, conversationId) => {
  return {
    ...initConversationMessages(),
    ...state.messagesByConversationId[conversationId]
  };
};
var updateConversationMessages = (state, conversationId, conversationMessages) => {
  return {
    ...state,
    messagesByConversationId: {
      ...state.messagesByConversationId,
      [conversationId]: conversationMessages
    }
  };
};
var removeConversationMessages = (state, conversationId) => {
  const {
    [conversationId]: _,
    ...restConversations
  } = state.messagesByConversationId;
  return {
    ...state,
    messagesByConversationId: {
      ...restConversations
    }
  };
};
var removeStatusFromLastOutboundMessage = (conversationMessages, messageContent) => {
  let updatedConversationMessages = {
    ...conversationMessages
  };
  let encounteredOutboundMessages = false;
  for (let i = conversationMessages.messages.length - 1; i >= 0; i -= 1) {
    const messageGroup = updatedConversationMessages.messages[i];
    if (messageGroup.__typename === "VirtualAgentControlOutboundMessageElementGroup") {
      const updatedMessageGroup = {
        ...messageGroup,
        messages: messageGroup.messages.map(message => message.status === (messageContent == null ? void 0 : messageContent.statusNotDelivered) ? message : {
          ...message,
          status: null
        })
      };
      const updatedMessages = [...updatedConversationMessages.messages.slice(0, i), updatedMessageGroup, ...updatedConversationMessages.messages.slice(i + 1)];
      updatedConversationMessages = {
        ...updatedConversationMessages,
        messages: updatedMessages
      };
      encounteredOutboundMessages = true;
    } else if (encounteredOutboundMessages) {
      break;
    }
  }
  return updatedConversationMessages;
};
var updatedConversationMessagesUtil = (typename, conversationMessages, messageIdsInSet, message) => {
  const messageIdSet = conversationMessages == null ? void 0 : conversationMessages.messageIdSet;
  let updatedConversationMessages;
  for (let i = conversationMessages.messages.length - 1; i >= 0; i -= 1) {
    const messageGroup = conversationMessages.messages[i];
    if (messageGroup.__typename === typename) {
      const messageIndex = messageGroup.messages.findIndex(innerMessage => messageIdsInSet.includes(innerMessage.metadata.messageId));
      if (messageIndex >= 0) {
        const updatedMessageGroup = {
          ...message
        };
        const updatedMessages = [...conversationMessages.messages.slice(0, i), updatedMessageGroup, ...conversationMessages.messages.slice(i + 1)];
        messageIdsInSet.forEach(msgId => messageIdSet == null ? void 0 : messageIdSet.delete(msgId));
        const messageIdsToInsert = message.messages.map(inbMsg => inbMsg.metadata.messageId);
        updatedConversationMessages = {
          ...conversationMessages,
          messages: updatedMessages,
          messageIdSet: /* @__PURE__ */new Set([...messageIdSet, ...messageIdsToInsert])
        };
        break;
      }
    }
  }
  return updatedConversationMessages;
};
function getTimestampFromMessage(message, isSubMessage) {
  var _a, _b;
  if ((message == null ? void 0 : message.__typename) === "VirtualAgentControlMessageSeparator") {
    return Number.isNaN(+new Date(message.primary)) ? message.timeStamp : message.primary;
  }
  const subMessage = isSubMessage ? message : (_a = message == null ? void 0 : message.messages) == null ? void 0 : _a[message.messages.length - 1];
  return (_b = subMessage == null ? void 0 : subMessage.metadata) == null ? void 0 : _b.timeStamp;
}
var sortNewMessageWithTimestamp = (currentMessages, newMessageGroup, isSubMessages = false) => {
  const newMessageTS = getTimestampFromMessage(newMessageGroup, isSubMessages);
  const newMessageDateTime = new Date(newMessageTS).getTime();
  let indexToInsert = currentMessages.length;
  for (let i = currentMessages.length - 1; i >= 0; i -= 1) {
    const currentMessageGroup = currentMessages[i];
    const currentMessageTS = getTimestampFromMessage(currentMessageGroup, isSubMessages);
    const currentMessageDateTime = new Date(currentMessageTS).getTime();
    if (currentMessageDateTime <= newMessageDateTime) {
      break;
    }
    indexToInsert = i;
  }
  return [...currentMessages.slice(0, indexToInsert), newMessageGroup, ...currentMessages.slice(indexToInsert)];
};
var addMessage = (state, payload) => {
  var _a, _b, _c, _d;
  const {
    conversationId,
    message,
    messageContent
  } = payload || {};
  if (!conversationId || !message) {
    return state;
  }
  const conversationMessages = getConversationMessages(state, conversationId);
  const messageIdSet = conversationMessages == null ? void 0 : conversationMessages.messageIdSet;
  let updatedConversationMessages;
  let currentMessageId;
  if (isGroupableInboundMessage(message.__typename)) {
    const conversationMessagesWithStatusProcessed = removeStatusFromLastOutboundMessage(conversationMessages, messageContent);
    const currentMessages = [...conversationMessagesWithStatusProcessed.messages];
    const lastIdx = currentMessages.length - 1;
    const lastMessageGroup = currentMessages[lastIdx];
    if ((lastMessageGroup == null ? void 0 : lastMessageGroup.__typename) === "VirtualAgentControlInboundMessageGroup" && (lastMessageGroup == null ? void 0 : lastMessageGroup.eyebrow) === (message == null ? void 0 : message.eyebrow)) {
      currentMessageId = (_a = message.metadata) == null ? void 0 : _a.messageId;
      if (messageIdSet.has(currentMessageId)) {
        return state;
      }
      const inboundGroup = {
        ...lastMessageGroup,
        messages: sortNewMessageWithTimestamp(lastMessageGroup.messages, message, true),
        timeStamp: (_c = (_b = message.metadata) == null ? void 0 : _b.timeStamp) != null ? _c : lastMessageGroup.timeStamp
      };
      updatedConversationMessages = {
        ...conversationMessagesWithStatusProcessed,
        messages: sortNewMessageWithTimestamp(currentMessages.slice(0, lastIdx), inboundGroup),
        messageIdSet: /* @__PURE__ */new Set([...messageIdSet, currentMessageId])
      };
    } else {
      currentMessageId = (_d = message.metadata) == null ? void 0 : _d.messageId;
      updatedConversationMessages = {
        ...conversationMessagesWithStatusProcessed,
        messages: sortNewMessageWithTimestamp(conversationMessagesWithStatusProcessed.messages, createInboundGroup(message)),
        messageIdSet: /* @__PURE__ */new Set([...messageIdSet, currentMessageId])
      };
    }
  } else if (message.__typename === "VirtualAgentControlInboundMessageGroup") {
    const conversationMessagesWithStatusProcessed = removeStatusFromLastOutboundMessage(conversationMessages, messageContent);
    const inboundMessageGroup = message;
    const messageIdsInSet = inboundMessageGroup.messages.filter(inbMsg => messageIdSet == null ? void 0 : messageIdSet.has(inbMsg.metadata.messageId)).map(msg => msg.metadata.messageId);
    if (messageIdsInSet.length === inboundMessageGroup.messages.length) {
      updatedConversationMessages = {
        ...conversationMessagesWithStatusProcessed
      };
    } else if (messageIdsInSet.length && messageIdsInSet.length < inboundMessageGroup.messages.length) {
      updatedConversationMessages = updatedConversationMessagesUtil("VirtualAgentControlInboundMessageGroup", conversationMessagesWithStatusProcessed, messageIdsInSet, message);
    } else {
      const messageIds = inboundMessageGroup.messages.map(msg => msg.metadata.messageId);
      updatedConversationMessages = {
        ...conversationMessagesWithStatusProcessed,
        messages: sortNewMessageWithTimestamp(conversationMessagesWithStatusProcessed.messages, message),
        messageIdSet: /* @__PURE__ */new Set([...messageIdSet, ...messageIds])
      };
    }
  } else if (message.__typename === "VirtualAgentControlOutboundMessageElementGroup") {
    const outboundMessageElementGroup = message;
    const messageIds = outboundMessageElementGroup.messages.map(msg => msg.metadata.messageId);
    const messageIdsInSet = messageIds.filter(msgId => messageIdSet == null ? void 0 : messageIdSet.has(msgId));
    if (messageIds.length === messageIdsInSet.length) {
      updatedConversationMessages = {
        ...conversationMessages
      };
    } else if (messageIdsInSet.length && messageIdsInSet.length < messageIds.length) {
      updatedConversationMessages = updatedConversationMessagesUtil("VirtualAgentControlOutboundMessageElementGroup", conversationMessages, messageIdsInSet, message);
    } else {
      updatedConversationMessages = {
        ...conversationMessages,
        messages: sortNewMessageWithTimestamp(conversationMessages.messages, message),
        messageIdSet: /* @__PURE__ */new Set([...messageIdSet, ...outboundMessageElementGroup.messages.map(msg => msg.metadata.messageId)])
      };
    }
  } else {
    updatedConversationMessages = {
      ...conversationMessages,
      messages: sortNewMessageWithTimestamp(conversationMessages.messages, message)
    };
  }
  return updateConversationMessages(state, conversationId, updatedConversationMessages);
};
var clearMessages = (state, payload) => {
  const {
    conversationId
  } = payload || {};
  if (!conversationId) {
    return state;
  }
  const conversationMessages = getConversationMessages(state, conversationId);
  if (conversationMessages.messages.length === 0) {
    return state;
  }
  const updatedConversationMessages = {
    ...conversationMessages,
    messages: []
  };
  return updateConversationMessages(state, conversationId, updatedConversationMessages);
};
var updateOutboundMessage = (state, payload) => {
  var _a, _b;
  const {
    conversationId,
    umid,
    outboundMessageData
  } = payload || {};
  if (conversationId && umid && outboundMessageData) {
    const conversationMessages = getConversationMessages(state, conversationId);
    const {
      messages
    } = conversationMessages;
    let messageIndex;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const messageGroup = messages[i];
      if (messageGroup.__typename === "VirtualAgentControlOutboundMessageElementGroup") {
        messageIndex = messageGroup.messages.findIndex(innerMessage => innerMessage.metadata.messageId === umid);
        if (messageIndex >= 0) {
          const outboundMessage = {
            ...messageGroup.messages[messageIndex],
            status: outboundMessageData.status,
            messageId: outboundMessageData.messageId
          };
          const updatedMessageGroup = {
            ...messageGroup,
            messages: [...messageGroup.messages.slice(0, messageIndex), outboundMessage, ...messageGroup.messages.slice(messageIndex + 1)]
          };
          const updatedMessages = outboundMessageData.deliveryFailed && outboundMessageData.status === ((_a = outboundMessageData.messageContent) == null ? void 0 : _a.statusDelivered) ? [...messages.slice(0, i), ...messages.slice(i + 1), updatedMessageGroup] : [...messages.slice(0, i), updatedMessageGroup, ...messages.slice(i + 1)];
          const updatedMessageIdSet = /* @__PURE__ */new Set([...conversationMessages.messageIdSet]);
          if (updatedMessageIdSet.has(umid) && outboundMessageData.status !== ((_b = outboundMessageData.messageContent) == null ? void 0 : _b.statusNotDelivered)) {
            updatedMessageIdSet.delete(umid);
            updatedMessageIdSet.add(outboundMessageData.messageId);
          }
          const updatedConversationMessages = {
            ...conversationMessages,
            messages: updatedMessages,
            messageIdSet: updatedMessageIdSet
          };
          return updateConversationMessages(state, conversationId, updatedConversationMessages);
        }
      }
    }
  }
  return state;
};
var setQuickReplies = (state, payload) => {
  const {
    conversationId,
    message,
    messageId,
    analytics
  } = payload || {};
  if (!conversationId || !message) {
    return state;
  }
  return {
    ...state,
    messagesByConversationId: {
      ...state.messagesByConversationId,
      [conversationId]: {
        ...getConversationMessages(state, conversationId),
        quickReplies: [...message],
        quickReplyAnalytics: analytics,
        ...(messageId && {
          lastQuickMessageId: messageId
        })
      }
    }
  };
};
var uploadAttachment = (state, payload) => {
  var _a;
  const {
    conversationId,
    uniqueAttachmentId,
    message
  } = payload || {};
  if (!conversationId || !uniqueAttachmentId || !message) {
    return state;
  }
  const conversationMessages = getConversationMessages(state, conversationId);
  const currentAttachmentStatusMap = {
    ...state.attachmentStatusMap
  };
  const messageIdSet = conversationMessages == null ? void 0 : conversationMessages.messageIdSet;
  let currentMessageId;
  if (message.__typename === "VirtualAgentControlOutboundMessageElementGroup") {
    const outboundMessageElement = message;
    currentMessageId = (_a = outboundMessageElement.messages[0].metadata) == null ? void 0 : _a.messageId;
  }
  if (messageIdSet.has(currentMessageId)) {
    return state;
  }
  currentAttachmentStatusMap[uniqueAttachmentId] = {
    loading: true,
    operationType: AttachmentOperationTypes.UPLOAD
  };
  return {
    ...state,
    messagesByConversationId: {
      ...state.messagesByConversationId,
      [conversationId]: {
        ...conversationMessages,
        messages: [...conversationMessages.messages, message],
        messageIdSet: /* @__PURE__ */new Set([...messageIdSet, currentMessageId])
      }
    },
    attachmentStatusMap: currentAttachmentStatusMap
  };
};
var uploadAttachmentSuccess = (state, payload) => {
  if (!payload || !payload.uniqueAttachmentId || !payload.conversationId) {
    return state;
  }
  const conversationMessages = getConversationMessages(state, payload.conversationId);
  const {
    messages
  } = conversationMessages;
  const lastOutboundMessageGroupIdx = messages.findLastIndex(msg => {
    var _a;
    return msg.__typename === "VirtualAgentControlOutboundMessageElementGroup" && msg.messages[0].__typename === "VirtualAgentControlOutboundFileMessage" && ((_a = msg.messages[0].attachmentMetadata) == null ? void 0 : _a.uniqueAttachmentId) === payload.uniqueAttachmentId;
  });
  const lastOutboundMessage = messages[lastOutboundMessageGroupIdx];
  let lastFileMessage = lastOutboundMessage.messages[0];
  const {
    name,
    uniqueAttachmentId,
    size,
    mediaUrl,
    mediaType
  } = payload.attachmentMetadata;
  lastFileMessage = {
    ...lastFileMessage,
    attachmentMetadata: {
      ...lastFileMessage.attachmentMetadata,
      name,
      uniqueAttachmentId,
      size,
      mediaUrl,
      mediaType,
      __typename: "VirtualAgentControlAttachmentData"
    }
  };
  const currentAttachmentStatusMap = {
    ...state.attachmentStatusMap
  };
  const prevStatus = {
    ...currentAttachmentStatusMap[payload.uniqueAttachmentId]
  };
  if (currentAttachmentStatusMap[payload.uniqueAttachmentId]) {
    delete currentAttachmentStatusMap[payload.uniqueAttachmentId];
  }
  currentAttachmentStatusMap[payload.attachmentMetadata.uniqueAttachmentId] = {
    ...prevStatus,
    loading: payload.loading,
    isSuccess: payload.isSuccess
  };
  return {
    ...state,
    attachmentStatusMap: currentAttachmentStatusMap,
    messagesByConversationId: {
      ...state.messagesByConversationId,
      [payload.conversationId]: {
        ...conversationMessages,
        messages: [...messages.slice(0, lastOutboundMessageGroupIdx), {
          ...lastOutboundMessage,
          messages: [lastFileMessage]
        }, ...messages.slice(lastOutboundMessageGroupIdx + 1)]
      }
    }
  };
};
var uploadAttachmentFailure = (state, payload) => {
  if (!payload || !payload.uniqueAttachmentId) {
    return state;
  }
  const currentAttachmentStatusMap = {
    ...state.attachmentStatusMap
  };
  currentAttachmentStatusMap[payload.uniqueAttachmentId] = {
    ...currentAttachmentStatusMap[payload.uniqueAttachmentId],
    loading: payload.loading,
    hasError: payload.hasError
  };
  return {
    ...state,
    attachmentStatusMap: currentAttachmentStatusMap
  };
};
var updateOutboundMessageElement = (state, payload) => {
  var _a, _b;
  const {
    conversationId,
    umid,
    outboundMessageData
  } = payload || {};
  if (conversationId && umid && outboundMessageData) {
    const conversationMessages = getConversationMessages(state, conversationId);
    const {
      messages
    } = conversationMessages;
    let messageIndex;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const messageGroup = messages[i];
      if (messageGroup.__typename === "VirtualAgentControlOutboundMessageElementGroup") {
        messageIndex = messageGroup.messages.findIndex(innerMessage => innerMessage.metadata.messageId === umid);
        if (messageIndex >= 0) {
          const outboundMessageElement = {
            ...messageGroup.messages[messageIndex],
            status: outboundMessageData.status,
            messageId: outboundMessageData.messageId
          };
          const updatedMessageGroup = {
            ...messageGroup,
            messages: [...messageGroup.messages.slice(0, messageIndex), outboundMessageElement, ...messageGroup.messages.slice(messageIndex + 1)]
          };
          const updatedMessages = outboundMessageData.deliveryFailed && outboundMessageData.status === ((_a = outboundMessageData.messageContent) == null ? void 0 : _a.statusDelivered) ? [...messages.slice(0, i), ...messages.slice(i + 1), updatedMessageGroup] : [...messages.slice(0, i), updatedMessageGroup, ...messages.slice(i + 1)];
          const updatedMessageIdSet = /* @__PURE__ */new Set([...conversationMessages.messageIdSet]);
          if (updatedMessageIdSet.has(umid) && outboundMessageData.status !== ((_b = outboundMessageData.messageContent) == null ? void 0 : _b.statusNotDelivered)) {
            updatedMessageIdSet.delete(umid);
            updatedMessageIdSet.add(outboundMessageData.messageId);
          }
          const updatedConversationMessages = {
            ...conversationMessages,
            messages: updatedMessages,
            messageIdSet: updatedMessageIdSet
          };
          return updateConversationMessages(state, conversationId, updatedConversationMessages);
        }
      }
    }
  }
  return state;
};
var updateAttachmentStatusMap = (state, payload) => {
  if (!payload) {
    return state;
  }
  const currentAttachmentStatusMap = {
    ...state.attachmentStatusMap
  };
  currentAttachmentStatusMap[payload.uniqueAttachmentId] = payload.attachmentStatus;
  return {
    ...state,
    attachmentStatusMap: currentAttachmentStatusMap
  };
};
var updateUnreadMessageCount = (state, payload) => {
  const {
    conversationId
  } = payload || {};
  if (!conversationId) {
    return state;
  }
  const conversationMessages = getConversationMessages(state, conversationId);
  return updateConversationMessages(state, conversationId, {
    ...conversationMessages,
    unreadMessageCount: conversationMessages.unreadMessageCount + 1
  });
};
var updateIsIBMFlow = (state, payload) => {
  const {
    conversationId,
    isIBMFlow
  } = payload || {};
  if (!conversationId) {
    return state;
  }
  const conversationMessages = getConversationMessages(state, conversationId);
  return updateConversationMessages(state, conversationId, {
    ...conversationMessages,
    isIBMFlow
  });
};
var clearUnreadMessageCount = (state, payload) => {
  const {
    conversationId
  } = payload || {};
  if (!conversationId) {
    return state;
  }
  const conversationMessages = getConversationMessages(state, conversationId);
  return updateConversationMessages(state, conversationId, {
    ...conversationMessages,
    unreadMessageCount: 0
  });
};
var addMessageSeparator = (state, payload) => {
  const {
    conversationId,
    primary,
    timeStamp,
    participantId
  } = payload || {};
  const messageId = timeStamp != null ? timeStamp : new Date(primary).toLocaleDateString();
  if (!conversationId || !primary) {
    return state;
  }
  const conversationMessages = getConversationMessages(state, conversationId);
  if (conversationMessages.messageIdSet.has(messageId)) {
    return state;
  }
  return updateConversationMessages(state, conversationId, {
    ...conversationMessages,
    messages: sortNewMessageWithTimestamp(conversationMessages.messages, createMessageSeperator(primary, messageId, participantId)),
    messageIdSet: /* @__PURE__ */new Set([...conversationMessages.messageIdSet, messageId])
    // As message separator doesn't have a messageId, we are storing date as messageId
  });
};
var updateSeparatorMessageMarkers = (state, payload) => {
  var _a;
  const {
    conversationId,
    participantId,
    primary
  } = payload || {};
  if (!conversationId || !primary || !participantId) {
    return state;
  }
  const conversationMessages = getConversationMessages(state, conversationId);
  for (let i = conversationMessages.messages.length - 1; i >= 0; i -= 1) {
    const currentMessageGroup = conversationMessages.messages[i];
    if (currentMessageGroup.__typename === "VirtualAgentControlMessageSeparator" && ((_a = currentMessageGroup.meta) == null ? void 0 : _a.participantId) === participantId) {
      const newSeparatorMessageWithMarker = {
        ...currentMessageGroup,
        primary
      };
      return updateConversationMessages(state, conversationId, {
        ...conversationMessages,
        messages: [...conversationMessages.messages.slice(0, i), newSeparatorMessageWithMarker, ...conversationMessages.messages.slice(i + 1)]
      });
    }
  }
  return state;
};
var saveActiveConversationLastMessageTimeStamp = (state, payload) => {
  const {
    conversationId,
    timeStamp
  } = payload || {};
  if (!conversationId || !timeStamp) {
    return state;
  }
  const conversationMessages = getConversationMessages(state, conversationId);
  return updateConversationMessages(state, conversationId, {
    ...conversationMessages,
    lastReceivedMessageTimeStamp: timeStamp
  });
};
var setAnnounceMessageId = (state, payload) => {
  const {
    conversationId,
    messageId
  } = payload || {};
  if (!conversationId) {
    return state;
  }
  const conversationMessages = getConversationMessages(state, conversationId);
  return updateConversationMessages(state, conversationId, {
    ...conversationMessages,
    announceMessageId: messageId
  });
};
var updateFetchHistoryMessagesStatus = (state, payload) => {
  const {
    status,
    conversationId
  } = payload || {};
  if (!conversationId || status === void 0) {
    return state;
  }
  const conversationMessages = getConversationMessages(state, conversationId);
  return updateConversationMessages(state, conversationId, {
    ...conversationMessages,
    fetchHistoryMessagesStatus: status
  });
};
var addLoaderMessage = (state, payload) => {
  const {
    conversationId,
    message
  } = payload || {};
  if (!conversationId || !message || message.__typename !== "VirtualAgentControlMessageLoader") {
    return state;
  }
  const conversationMessages = getConversationMessages(state, conversationId);
  return updateConversationMessages(state, conversationId, {
    ...conversationMessages,
    messages: [message, ...conversationMessages.messages]
  });
};
var removeLoaderMessage = (state, payload) => {
  const {
    conversationId
  } = payload || {};
  if (!conversationId) {
    return state;
  }
  const conversationMessages = getConversationMessages(state, conversationId);
  const firstMessage = conversationMessages == null ? void 0 : conversationMessages.messages[0];
  if ((firstMessage == null ? void 0 : firstMessage.__typename) !== "VirtualAgentControlMessageLoader") {
    return state;
  }
  return updateConversationMessages(state, conversationId, {
    ...conversationMessages,
    messages: [...conversationMessages.messages.slice(1)]
  });
};
var addHistoryMessages = (state, payload) => {
  var _a, _b;
  const {
    conversationId,
    messages
  } = payload || {};
  if (!conversationId || !(messages == null ? void 0 : messages.length)) {
    return state;
  }
  const conversationMessages = getConversationMessages(state, conversationId);
  const lastTimeStampInMessages = (_a = messages.findLast(message => message.__typename === "VirtualAgentControlMessageSeparator")) == null ? void 0 : _a.timeStamp;
  if (conversationMessages == null ? void 0 : conversationMessages.messages.length) {
    const lastTimeStamp = (_b = conversationMessages.messages.find(message => message.__typename === "VirtualAgentControlMessageSeparator")) == null ? void 0 : _b.timeStamp;
    if (lastTimeStamp && lastTimeStampInMessages && new Date(lastTimeStamp).toLocaleDateString() === new Date(lastTimeStampInMessages).toLocaleDateString()) {
      return updateConversationMessages(state, conversationId, {
        ...conversationMessages,
        // This is an edge case scenario where history api may return duplicate separator , so we are slicing out the loader and last separator so we don't show duplicate separator.
        messages: [...messages, ...conversationMessages.messages.slice(2)]
      });
    }
  }
  return updateConversationMessages(state, conversationId, {
    ...conversationMessages,
    // Loader always sits in front inside messages state, so remove it and append the history messages in front.
    messages: [...messages, ...conversationMessages.messages.slice(1)]
  });
};
var updateMessageLoaderVisibility = (state, payload) => {
  const {
    conversationId,
    isMessageLoaderVisible
  } = payload || {};
  if (!conversationId) {
    return state;
  }
  const conversationMessages = getConversationMessages(state, conversationId);
  return updateConversationMessages(state, conversationId, {
    ...conversationMessages,
    isMessageLoaderVisible
  });
};
var updateConversationId = (state, payload) => {
  const {
    conversationId,
    activeConversationId
  } = payload || {};
  if (!conversationId || !activeConversationId || activeConversationId === conversationId) {
    return state;
  }
  const conversationMessages = getConversationMessages(state, activeConversationId);
  const updatedConversationMessages = updateConversationMessages(state, conversationId, {
    ...conversationMessages
  });
  return removeConversationMessages(updatedConversationMessages, activeConversationId);
};

// ../internal/src/store/messages/messages-state.tsx
var initMessagesState = {
  messagesByConversationId: {},
  attachmentStatusMap: {}
};
var MessagesState = /*#__PURE__*/function () {
  function MessagesState() {
    _classCallCheck(this, MessagesState);
    this.data = initMessagesState;
    this.stateFieldName = "messagesState";
    this.events = Object.values(MessagesStateEvent);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return _createClass(MessagesState, [{
    key: "update",
    value: function update(event, state, payload) {
      switch (event) {
        case "ADD_MESSAGE" /* ADD_MESSAGE */:
          return addMessage(state, payload == null ? void 0 : payload.data);
        case "CLEAR_MESSAGES" /* CLEAR_MESSAGES */:
          return clearMessages(state, payload == null ? void 0 : payload.data);
        case "UPDATE_OUTBOUND_MESSAGE" /* UPDATE_OUTBOUND_MESSAGE */:
          return updateOutboundMessage(state, payload == null ? void 0 : payload.data);
        case "SET_QUICK_REPLIES" /* SET_QUICK_REPLIES */:
          return setQuickReplies(state, payload == null ? void 0 : payload.data);
        case "UPLOAD_ATTACHMENT" /* UPLOAD_ATTACHMENT */:
          return uploadAttachment(state, payload == null ? void 0 : payload.data);
        case "UPLOAD_ATTACHMENT_SUCCESS" /* UPLOAD_ATTACHMENT_SUCCESS */:
          return uploadAttachmentSuccess(state, payload == null ? void 0 : payload.data);
        case "UPLOAD_ATTACHMENT_FAILURE" /* UPLOAD_ATTACHMENT_FAILURE */:
          return uploadAttachmentFailure(state, payload == null ? void 0 : payload.data);
        case "UPDATE_OUTBOUND_MESSAGE_ELEMENT" /* UPDATE_OUTBOUND_MESSAGE_ELEMENT */:
          return updateOutboundMessageElement(state, payload == null ? void 0 : payload.data);
        case "UPDATE_ATTACHMENT_STATUS_MAP" /* UPDATE_ATTACHMENT_STATUS_MAP */:
          return updateAttachmentStatusMap(state, payload == null ? void 0 : payload.data);
        case "SET_UNREAD_MESSAGE_COUNT" /* SET_UNREAD_MESSAGE_COUNT */:
          return updateUnreadMessageCount(state, payload == null ? void 0 : payload.data);
        case "SET_IBM_FLOW" /* SET_IBM_FLOW */:
          return updateIsIBMFlow(state, payload == null ? void 0 : payload.data);
        case "CLEAR_UNREAD_MESSAGE_COUNT" /* CLEAR_UNREAD_MESSAGE_COUNT */:
          return clearUnreadMessageCount(state, payload == null ? void 0 : payload.data);
        case "SET_ANNOUNCE_MESSAGE_ID" /* SET_ANNOUNCE_MESSAGE_ID */:
          return setAnnounceMessageId(state, payload == null ? void 0 : payload.data);
        case "SET_LAST_MESSAGE_TIMESTAMP" /* SET_LAST_MESSAGE_TIMESTAMP */:
          return saveActiveConversationLastMessageTimeStamp(state, payload == null ? void 0 : payload.data);
        case "ADD_SEPERATOR_MESSAGE" /* ADD_SEPERATOR_MESSAGE */:
          return addMessageSeparator(state, payload == null ? void 0 : payload.data);
        case "FETCH_HISTORY_MESSAGES_STATUS" /* FETCH_HISTORY_MESSAGES_STATUS */:
          return updateFetchHistoryMessagesStatus(state, payload == null ? void 0 : payload.data);
        case "ADD_HISTORY_MESSAGES" /* ADD_HISTORY_MESSAGES */:
          return addHistoryMessages(state, payload == null ? void 0 : payload.data);
        case "REMOVE_LOADER_MESSAGE" /* REMOVE_LOADER_MESSAGE */:
          return removeLoaderMessage(state, payload == null ? void 0 : payload.data);
        case "ADD_LOADER_MESSAGE" /* ADD_LOADER_MESSAGE */:
          return addLoaderMessage(state, payload == null ? void 0 : payload.data);
        case "UPDATE_MESSAGE_LOADER_VISIBILITY" /* UPDATE_MESSAGE_LOADER_VISIBILITY */:
          return updateMessageLoaderVisibility(state, payload == null ? void 0 : payload.data);
        case "RELOCATE_MESSAGES_TO_NEW_CONV" /* RELOCATE_MESSAGES_TO_NEW_CONV */:
          return updateConversationId(state, payload == null ? void 0 : payload.data);
        case "UPDATE_PARTICIPANT_MARKERS" /* UPDATE_PARTICIPANT_MARKERS */:
          return updateSeparatorMessageMarkers(state, payload == null ? void 0 : payload.data);
        default:
          return state;
      }
    }
  }]);
}();

// ../internal/src/store/orchestrator-state/constants.ts
var OrchestratorStateEvent = /* @__PURE__ */(OrchestratorStateEvent2 => {
  OrchestratorStateEvent2["SET_VAC_CONFIGURATION"] = "SET_VAC_CONFIGURATION";
  OrchestratorStateEvent2["SET_INIT_STATUS"] = "SET_INIT_STATUS";
  OrchestratorStateEvent2["SET_LAST_WEBSOCKET_DISCONNECT_TIME"] = "SET_LAST_WEBSOCKET_DISCONNECT_TIME";
  return OrchestratorStateEvent2;
})(OrchestratorStateEvent || {});

// ../internal/src/store/orchestrator-state/reducers.ts
import isEqual5 from "lodash.isequal";
var setInitStatus = (state, initStatus) => {
  if (initStatus && initStatus !== state.initializationStatus) {
    return {
      ...state,
      initializationStatus: initStatus
    };
  }
  return state;
};
var setVacConfig = (state, data) => {
  if (data && !isEqual5(data, state.vacConfig)) {
    return {
      ...state,
      vacConfig: {
        authToken: data.authToken,
        websocket: data.websocket
      }
    };
  }
  return state;
};
var setLastWebsocketDisconnectTime = (state, time) => {
  if (time && !isEqual5(time, state.lastWebsocketDisconnectTime)) {
    return {
      ...state,
      lastWebsocketDisconnectTime: time
    };
  }
  return state;
};

// ../internal/src/store/orchestrator-state/orchestrator-state.ts
var initOrchestratorState = {
  vacConfig: {
    authToken: "",
    websocket: null
  },
  initializationStatus: "RUNNING" /* RUNNING */,
  lastWebsocketDisconnectTime: null
};
var OrchestratorState = /*#__PURE__*/function () {
  function OrchestratorState() {
    _classCallCheck(this, OrchestratorState);
    this.data = initOrchestratorState;
    this.stateFieldName = "orchestratorState";
    this.events = Object.values(OrchestratorStateEvent);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return _createClass(OrchestratorState, [{
    key: "update",
    value: function update(event, state, payload) {
      switch (event) {
        case "SET_INIT_STATUS" /* SET_INIT_STATUS */:
          return setInitStatus(state, payload.data);
        case "SET_VAC_CONFIGURATION" /* SET_VAC_CONFIGURATION */:
          return setVacConfig(state, payload.data);
        case "SET_LAST_WEBSOCKET_DISCONNECT_TIME" /* SET_LAST_WEBSOCKET_DISCONNECT_TIME */:
          return setLastWebsocketDisconnectTime(state, payload.data);
        default:
          return state;
      }
    }
  }]);
}();

// ../internal/src/store/participant-events/constants.tsx
var ParticipantStateEvent = /* @__PURE__ */(ParticipantStateEvent2 => {
  ParticipantStateEvent2["CLEAR_ALL_TYPING_INDICATORS"] = "CLEAR_ALL_TYPING_INDICATORS";
  ParticipantStateEvent2["SET_TYPING_INDICATOR"] = "SET_TYPING_INDICATOR";
  ParticipantStateEvent2["SET_PARTICIPANTS"] = "SET_PARTICIPANTS";
  ParticipantStateEvent2["MARK_PARTICIPANT_LEFT"] = "MARK_PARTICIPANT_LEFT";
  return ParticipantStateEvent2;
})(ParticipantStateEvent || {});

// ../internal/src/store/participant-events/reducers.tsx
import isEqual6 from "lodash.isequal";
var updateActiveParticipantList = (activeParticipantList, indicator) => {
  const authorIndex = activeParticipantList.findIndex(param => param.messageAuthor === indicator.messageAuthor && param.conversationId === indicator.conversationId);
  const updatedactiveParticipantList = [...activeParticipantList];
  if (indicator.indicatorType === "NONE" /* NONE */) {
    if (authorIndex !== -1) {
      updatedactiveParticipantList.splice(authorIndex, 1);
    }
  } else if (indicator.indicatorType === "TYPING" /* TYPING */) {
    if (!indicator.start && authorIndex !== -1) {
      updatedactiveParticipantList.splice(authorIndex, 1);
    } else if (indicator.start && authorIndex === -1) {
      const data = {
        indicatorType: "TYPING" /* TYPING */,
        messageAuthor: indicator.messageAuthor,
        start: true,
        conversationId: indicator.conversationId
      };
      updatedactiveParticipantList.push(data);
    }
  } else if (indicator.indicatorType === "ACTIVE" /* ACTIVE */) {
    if (indicator.isActivityIndicator && authorIndex !== -1) {
      updatedactiveParticipantList.splice(authorIndex, 1);
    } else if (indicator.isActivityIndicator) {
      const data = {
        indicatorType: "ACTIVE" /* ACTIVE */,
        activeIndicatorMessage: indicator.activeIndicatorMessage,
        messageAuthor: indicator.messageAuthor,
        isActivityIndicator: indicator.isActivityIndicator,
        conversationId: indicator.conversationId,
        eyebrow: indicator.eyebrow
      };
      updatedactiveParticipantList.push(data);
    }
  }
  return updatedactiveParticipantList;
};
var resetTypingIndicator = state => {
  return {
    ...state,
    activityIndicatorParticipantList: []
  };
};
var setTypingIndicator = (state, actionData) => {
  const {
    typingIndicator
  } = actionData || {};
  if (!typingIndicator) {
    return state;
  }
  return {
    ...state,
    activityIndicatorParticipantList: updateActiveParticipantList(state.activityIndicatorParticipantList, typingIndicator)
  };
};
var markParticipantLeft = (state, actionData) => {
  const {
    participantId
  } = actionData;
  if (!participantId) {
    return state;
  }
  const participantsCopy = {
    ...state.participants
  };
  if (participantsCopy[participantId]) {
    participantsCopy[participantId] = {
      ...participantsCopy[participantId],
      participantState: "LEFT" /* LEFT */
    };
  }
  return {
    ...state,
    participants: participantsCopy
  };
};
var setParticipants = (state, actionData) => {
  const {
    conversationId,
    participants
  } = actionData || {};
  if (!conversationId || !(participants == null ? void 0 : participants.length)) {
    return state;
  }
  const changedParticipants = {};
  actionData.participants.forEach(participant => {
    const {
      cpUserId,
      participantType,
      id,
      user,
      participantState
    } = participant;
    const mappedParticipant = {
      conversationId,
      cpUserId,
      participantType,
      id,
      user,
      participantState
    };
    if (!state.participants[id] || !isEqual6(state.participants[id], mappedParticipant)) {
      changedParticipants[id] = mappedParticipant;
    }
  });
  if (!Object.keys(changedParticipants).length) {
    return state;
  }
  return {
    ...state,
    participants: {
      ...state.participants,
      ...changedParticipants
    }
  };
};

// ../internal/src/store/participant-events/participant-state.tsx
var initParticipantState = {
  participants: {},
  activityIndicatorParticipantList: []
};
var ParticipantState2 = /*#__PURE__*/function () {
  function ParticipantState2() {
    _classCallCheck(this, ParticipantState2);
    this.data = initParticipantState;
    this.stateFieldName = "participantState";
    this.events = Object.values(ParticipantStateEvent);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return _createClass(ParticipantState2, [{
    key: "update",
    value: function update(event, state, payload) {
      switch (event) {
        case "CLEAR_ALL_TYPING_INDICATORS" /* CLEAR_ALL_TYPING_INDICATORS */:
          return resetTypingIndicator(state);
        case "SET_TYPING_INDICATOR" /* SET_TYPING_INDICATOR */:
          return setTypingIndicator(state, payload == null ? void 0 : payload.data);
        case "SET_PARTICIPANTS" /* SET_PARTICIPANTS */:
          return setParticipants(state, payload == null ? void 0 : payload.data);
        case "MARK_PARTICIPANT_LEFT" /* MARK_PARTICIPANT_LEFT */:
          return markParticipantLeft(state, payload == null ? void 0 : payload.data);
        default:
          return state;
      }
    }
  }]);
}();

// ../internal/src/store/vac-state-provider.tsx
var VacStateProvider = buildStateProvider(new MessagesState(), new ConversationsState(), new ParticipantState2(), new ErrorsState(), new LoadingState(), new ChatInstancesState(), new OrchestratorState());
var withVacStateProvider = WrappedComponent => {
  const Wrapper = props => {
    return /* @__PURE__ */React2.createElement(VacStateProvider, null, /* @__PURE__ */React2.createElement(WrappedComponent, {
      ...props
    }));
  };
  Wrapper.displayName = "withVacStateProvider";
  return Wrapper;
};

// src/components/index.ts
import { withTimers } from "@shared-ui/retail-virtual-agent-providers";

// src/components/vac-orchestrator/vac-orchestrator.tsx
import React7 from "react";
import Omit2 from "lodash.omit";
import { gql as gql3, useQuery } from "@shared-ui/apollo-client-wrapper";
import { useBexApi as useBexApi4 } from "@shared-ui/bex-api-context";
import { useSignalPublisher as useSignalPublisher3 } from "@shared-ui/core-signal-manager/context";
import { useProvidedState as useProvidedState4 } from "@shared-ui/provided-state-context";
import { VacOrchestratorQuerySignal } from "@shared-ui/retail-signals-virtual-agent";
import { TIMERS, useTimers } from "@shared-ui/retail-virtual-agent-providers";

// src/components/utils/utils.tsx
import { VacInitializationErrorSignal } from "@shared-ui/retail-signals-virtual-agent";
var getErrorHandler = (dispatch, publishSignal) => {
  return error => {
    publishSignal(new VacInitializationErrorSignal("VAC_INITIALIZATION_ERROR" /* VAC_InitializationError */, null));
    dispatch({
      type: "SET_INIT_STATUS" /* SET_INIT_STATUS */,
      payload: "FAILED" /* FAILED */
    });
  };
};

// src/components/heartbeat-manager/heartbeat-manager.tsx
import { useCallback, useEffect, useRef, useState } from "react";
import { useSignalSubscriber as useSignalSubscriber2 } from "@shared-ui/core-signal-manager/context";
import { useProvidedState } from "@shared-ui/provided-state-context";
import { VacConnectedSignal, VacDisconnectedSignal, VacStartHeartbeatSignal } from "@shared-ui/retail-signals-virtual-agent";

// src/hooks/use-send-orchestrator-event.tsx
import React3 from "react";
import { gql, useMutation } from "@shared-ui/apollo-client-wrapper";
import { useBexApi } from "@shared-ui/bex-api-context";
var __SendEventMutation = gql`
  mutation SendOrchestratorEvent(
    $authenticationConfig: AuthenticationConfigInput
    $context: ContextInput!
    $conversationContext: ConversationContextInput!
    $event: VirtualAgentControlConversationEventInput!
  ) {
    sendConversationEvent(authenticationConfig: $authenticationConfig, context: $context, conversationContext: $conversationContext, event: $event) {
      retryText
    }
  }
`;
function useSendOrchestratorEvent(onCompleted, onError) {
  const [executeMutation] = useMutation(__SendEventMutation, {
    onCompleted,
    onError
  });
  const {
    context
  } = useBexApi();
  const sendEvent = React3.useCallback(request => {
    const sendEventData = {
      variables: {
        ...request,
        context
      }
    };
    return executeMutation(sendEventData);
  }, [context, executeMutation]);
  return [sendEvent];
}

// src/components/heartbeat-manager/heartbeat-manager.tsx
function getSubscriptionExistsPredicate(conversationContext) {
  return subscription => subscription.conversationContext.conversationId === conversationContext.conversationId && subscription.conversationContext.participantId === conversationContext.participantId;
}
var HeartbeatManager = ({
  onSendEventCompleted,
  onSendEventError
}) => {
  var _a, _b, _c;
  const [state] = useProvidedState();
  const [registry, setRegistry] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [hasAuthError, setHasAuthError] = useState(false);
  const onError = useCallback(error => {
    var _a2;
    if ((_a2 = error.graphQLErrors) == null ? void 0 : _a2.some(err => err.extensions.event.message.includes("Unauthorized"))) {
      setHasAuthError(true);
    }
    onSendEventError == null ? void 0 : onSendEventError(error);
  }, [setHasAuthError, onSendEventError]);
  const [sendEvent] = useSendOrchestratorEvent(onSendEventCompleted, onError);
  const timerRef = useRef();
  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = void 0;
    }
  };
  const authToken = (_b = (_a = state == null ? void 0 : state.orchestratorState) == null ? void 0 : _a.vacConfig) == null ? void 0 : _b.authToken;
  useEffect(() => {
    if (isConnected && !hasAuthError && registry.length > 0 && !timerRef.current) {
      const {
        interval
      } = registry[0];
      const sendHeartbeats = () => {
        registry.forEach(subscription => {
          const eventRequest = {
            conversationContext: subscription.conversationContext,
            event: {
              eventType: "HEARTBEAT" /* HEARTBEAT */
            },
            authenticationConfig: {
              authToken,
              channelOriginId: subscription.channelOriginId
            }
          };
          sendEvent(eventRequest);
        });
      };
      sendHeartbeats();
      timerRef.current = setInterval(sendHeartbeats, interval);
    } else if (hasAuthError || !isConnected) {
      clearTimer();
    }
    return () => {
      clearTimer();
    };
  }, [isConnected, hasAuthError, registry, authToken, sendEvent]);
  useSignalSubscriber2("VAC_DISCONNECTED" /* VAC_Disconnected */, VacDisconnectedSignal, {
    onSuccess: () => {
      setIsConnected(false);
    }
  });
  useSignalSubscriber2("VAC_CONNECTED" /* VAC_Connected */, VacConnectedSignal, {
    onSuccess: () => {
      setIsConnected(true);
    }
  });
  useSignalSubscriber2("VAC_START_HEARTBEAT" /* VAC_StartHeartbeat */, VacStartHeartbeatSignal, {
    onSuccess: signal => {
      const {
        conversationContext
      } = signal.payload;
      setRegistry(prevRegistry => prevRegistry.find(getSubscriptionExistsPredicate(conversationContext)) ? prevRegistry : [...prevRegistry, signal.payload]);
    }
  });
  if ((_c = state == null ? void 0 : state.conversationsState) == null ? void 0 : _c.conversationsById) {
    const registryCopy = registry.filter(entry => {
      var _a2;
      return ((_a2 = state.conversationsState.conversationsById[entry.conversationContext.conversationId]) == null ? void 0 : _a2.state) !== "CLOSED" /* CLOSED */;
    });
    if (registryCopy.length !== registry.length) {
      setRegistry(registryCopy);
      clearTimer();
    }
  }
  return null;
};
HeartbeatManager.displayName = "HeartbeatManager";
var heartbeat_manager_default = HeartbeatManager;

// src/components/websocket-connector/websocket-connector.tsx
import React6 from "react";
import { useBexApi as useBexApi3 } from "@shared-ui/bex-api-context";
import { useSignalPublisher as useSignalPublisher2, useSignalSubscriber as useSignalSubscriber3 } from "@shared-ui/core-signal-manager/context";
import { useLoggerEvents } from "@shared-ui/logger-context";
import { useProvidedState as useProvidedState3 } from "@shared-ui/provided-state-context";
import { VacChatMessageReceivedSignal as VacChatMessageReceivedSignal2, VacChatWindowVisibleSignal, VacConnectedSignal as VacConnectedSignal3, VacConnectWebsocketSignal as VacConnectWebsocketSignal2, VacConversationReadSignal, VacNotificationSoundSignal, VacParticipantEventsSignal, VacReconnectSignal } from "@shared-ui/retail-signals-virtual-agent";

// ../internal/src/helpers/date-utils.ts
import { useBexApi as useBexApi2 } from "@shared-ui/bex-api-context";

// ../internal/src/hooks/use-prefetch-localization-content.tsx
import React5 from "react";
import Omit from "lodash.omit";
import { gql as gql2, useApolloClient } from "@shared-ui/apollo-client-wrapper";
import { useProvidedState as useProvidedState2 } from "@shared-ui/provided-state-context";
var usePrefetchLocalizationContent = localiziedStringKey => {
  var _a, _b;
  const client = useApolloClient();
  const [state] = useProvidedState2();
  const localizedStringsValueSet = React5.useMemo(() => new Set(Object.values(LocalizationContentKeys)), []);
  if (localiziedStringKey === LocalizationContentKeys.ERROR) {
    const errorStrings = (_b = (_a = state.errorsState) == null ? void 0 : _a.orchestrator) == null ? void 0 : _b.offlineLocalizedErrorStrings;
    return errorStrings;
  }
  const localizationFragment = gql2`
    fragment VirtualAgentControlLocalizationContentFragment on VirtualAgentControlLocalizationContent {
      attachment {
        downloadError
        downloadProcessing
        downloadSuccess
        uploadError
        uploadFileSizeError
        uploadProcessing
        uploadSuccess
        outboundAttachmentAccessibilityLabelTemplate
      }
      dialog {
        closeWebviewWindow
      }
      activityIndicator {
        activityIndicatorMessage
        humanTyping
        multipleParticipantsTyping
        virtualAgentTyping
        typingIndicatorAccessibilityAnnouncementTemplate
      }
      error {
        havingTroubleLoading
        lostConnection
        refreshOrReconnect
        refreshOrTryLater
        refreshToContinue
        sessionExpired
        somethingWentWrong
        tryToReconnect
        removeExcessCharacters
        removeSingleExcessCharacter
      }
      loading {
        downloadChatTranscript
      }
      message {
        separatorToday
        separatorYesterday
        statusNotDelivered
        statusSending
        statusRetry
        statusDelivered
        dcfUnavailableLine1
        dcfUnavailableLine2
        timestampAccessibilityLabelTemplate
        messagesLoadedNotificationAnnouncement
      }
      participant {
        typeHumanAgent
        eventJoined
        eventLeft
        typeSupplier
        typeTraveler
        typeVirtualAgent
        outboundMessageContextAccessibilityLabel
      }
    }
  `;
  const cachedLocalizedStringsData = client.readFragment({
    id: "VirtualAgentControlLocalizationContent:vac-localization",
    fragment: localizationFragment
  });
  if (!cachedLocalizedStringsData || !localizedStringsValueSet.has(localiziedStringKey)) {
    return null;
  }
  return Omit(cachedLocalizedStringsData[localiziedStringKey], ["__typename"]);
};
var use_prefetch_localization_content_default = usePrefetchLocalizationContent;

// ../internal/src/helpers/date-utils.ts
function toDate(argument) {
  const argStr = Object.prototype.toString.call(argument);
  if (argument instanceof Date || typeof argument === "object" && argStr === "[object Date]") {
    return new argument.constructor(argument.getTime());
  }
  if (typeof argument === "number" || argStr === "[object Number]") {
    return new Date(argument);
  }
  return /* @__PURE__ */new Date(NaN);
}
function startOfDayUTC(date) {
  const _date = toDate(date);
  _date.setUTCHours(0, 0, 0, 0);
  return _date;
}

// ../internal/src/helpers/utils.tsx
import { liveAnnounce } from "@egds/react-core/utils";
var parseJson = json => {
  try {
    return JSON.parse(json);
  } catch (err) {
    return void 0;
  }
};
var getFormattedMessage = message => {
  const {
    data
  } = message || {};
  const {
    messageType,
    text
  } = data || {};
  if (messageType in MessageTypes && text) {
    return parseJson(text);
  }
  return void 0;
};
var isInboundMessage = parsedMessage => {
  const validTypes = ["VirtualAgentControlInboundTextMessage", "VirtualAgentControlInboundDynamicCardMessage", "VirtualAgentControlInboundCardMessage", "VirtualAgentControlInboundMessageGroup", "VirtualAgentControlInboundFileMessage"];
  return validTypes.includes(parsedMessage == null ? void 0 : parsedMessage.__typename);
};
var getFormattedChatFooterState = message => {
  const {
    data
  } = message || {};
  const {
    messageType,
    text
  } = data || {};
  if (messageType in MessageTypes && text) {
    const parseMessage = parseJson(message.data.text);
    const {
      stateUpdates: {
        chatInput = {}
      } = {}
    } = parseMessage || {};
    const {
      chatInputStatus,
      interactionMode
    } = chatInput || {};
    return {
      chatInputStatus,
      interactionMode
    };
  }
};
var getQuickReplies = message => {
  const {
    data
  } = message || {};
  const {
    messageType,
    text
  } = data || {};
  if (messageType in MessageTypes && text) {
    const formattedMessage = parseJson(text);
    const {
      quickReplies = []
    } = formattedMessage != null ? formattedMessage : {};
    let analytics = null;
    if ((formattedMessage == null ? void 0 : formattedMessage.__typename) === "VirtualAgentControlInboundTextMessage" || (formattedMessage == null ? void 0 : formattedMessage.__typename) === "VirtualAgentControlInboundDynamicCardMessage" || (formattedMessage == null ? void 0 : formattedMessage.__typename) === "VirtualAgentControlInboundQuickReplyMessage") {
      analytics = (formattedMessage == null ? void 0 : formattedMessage.analytics) || null;
    }
    return {
      quickReplies,
      analytics
    };
  }
  return {};
};
var getOutboundMessage = message => {
  if (!message) {
    return null;
  }
  return {
    messages: [message],
    status: null,
    __typename: "VirtualAgentControlOutboundMessageElementGroup"
  };
};
var getTypingIndicatorEvent = (typingIndicator, conversationId) => {
  return {
    indicatorType: "TYPING" /* TYPING */,
    start: typingIndicator.start,
    messageAuthor: typingIndicator.author,
    conversationId
  };
};
var getActivityIndicatorParams = (parsedMessage, conversationId) => {
  return {
    indicatorType: "ACTIVE" /* ACTIVE */,
    activeIndicatorMessage: parsedMessage.primary,
    messageAuthor: parsedMessage.metadata.messageAuthor,
    isActivityIndicator: parsedMessage.metadata.isActivityIndicatorMessage,
    eyebrow: parsedMessage.eyebrow,
    conversationId
  };
};
var getClearIndicatorParams = (message, conversationId) => {
  return {
    indicatorType: "NONE" /* NONE */,
    start: false,
    messageAuthor: message.author,
    conversationId
  };
};
var parseWebsocketParticipants = participant => {
  if (!participant) {
    return [];
  }
  const {
    user = {},
    conversationId = null,
    cpUserId = null,
    participantId = null,
    participantType = null,
    leaveContent = null,
    updateContent,
    joinContent = null
  } = participant;
  let participantState = null;
  if (joinContent !== null || updateContent !== null) {
    participantState = "JOINED" /* JOINED */;
  } else if (leaveContent !== null) {
    participantState = "LEFT" /* LEFT */;
  }
  const participantUser = {
    avatarUrl: user.avatarUrl || null,
    firstName: user.firstName || null,
    lastName: user.lastName || null,
    preferredName: user.preferredName || null
  };
  return [{
    conversationId,
    cpUserId,
    id: participantId,
    participantType,
    user: participantUser,
    participantState
  }];
};
function localizeTemplateLiteral(localizationString, literal, value) {
  if (!localizationString) {
    return "";
  }
  return localizationString.replace(`\${${literal}}`, value);
}
function getPartcipantDisplayName(participant) {
  var _a;
  const user = participant == null ? void 0 : participant.user;
  if (!user) {
    return void 0;
  }
  if ((_a = user.attributes) == null ? void 0 : _a.displayNameOverride) {
    return user.attributes.displayNameOverride;
  }
  if (participant.participantType === "Supplier" /* Supplier */) {
    return user.preferredName;
  }
  if (user.firstName) {
    return user.firstName;
  }
  if (user.preferredName) {
    return user.preferredName;
  }
  return user.userName;
}

// src/components/utils/log-events.ts
import { SystemEvent, SystemEventLevel } from "@shared-ui/logger-context";
var PREFIX = "VacLogging";
var COMPONENT_NAME = "VacOrchestrator";
var LogEvents = {
  WEBSOCKET_MESSAGE_PARSING_ERROR: new SystemEvent(SystemEventLevel.WARN, `${PREFIX}.${COMPONENT_NAME}.websocket.messageParsing.error`)
};

// src/components/utils/parse-websocket-message.ts
async function deflateString(compressedString) {
  const compressedBytes = new Uint8Array(compressedString.split("").map(char => char.charCodeAt(0)));
  const compressedStream = new ReadableStream({
    start(controller) {
      controller.enqueue(compressedBytes);
      controller.close();
    }
  });
  const decompressionStream = new DecompressionStream("deflate");
  const reader = compressedStream.pipeThrough(decompressionStream).getReader();
  let result = "";
  const textDecoder = new TextDecoder();
  while (true) {
    const {
      done,
      value
    } = await reader.read();
    if (done) {
      break;
    }
    result += textDecoder.decode(value);
  }
  return result;
}
async function getSerializedMessage(message) {
  if (message.startsWith("{")) {
    return message;
  }
  if (typeof window !== "undefined") {
    if (window.DecompressionStream) {
      return deflateString(atob(message));
    }
    throw new Error("Decompression not supported in this browser");
  }
  throw new Error("Unable to parse websocket message");
}
async function parseWebsocketMessage(message) {
  if (typeof message !== "string" || !message) {
    return null;
  }
  const serializedMessage = await getSerializedMessage(message);
  return JSON.parse(serializedMessage);
}

// src/components/utils/websocket-utils.ts
import { VacConnectedSignal as VacConnectedSignal2, VacDisconnectedSignal as VacDisconnectedSignal2 } from "@shared-ui/retail-signals-virtual-agent";
var refetchToken = (dispatch, refetch, errorStrings, variables) => {
  return async (tokenParams, callback) => {
    try {
      const {
        data: orchestratorData,
        error: orchestratorError
      } = await refetch({
        ...variables
      });
      if (orchestratorError) {
        dispatch({
          type: "SET_ORCHESTRATOR_ERROR" /* SET_ORCHESTRATOR_ERROR */,
          payload: {
            errorName: "fetchCredentialsFailed",
            errorStrings
          }
        });
        callback(orchestratorError.message, "");
      } else {
        callback("", orchestratorData.virtualAgentControlConfiguration.webSocket.ablyConfig.token);
      }
    } catch (err) {
      dispatch({
        type: "SET_ORCHESTRATOR_ERROR" /* SET_ORCHESTRATOR_ERROR */,
        payload: {
          errorName: "fetchCredentialsFailed",
          errorStrings
        }
      });
      callback(err.message, "");
    }
  };
};
var websocketConnectedCallback = (publishVacSignal, dispatch) => {
  return () => {
    publishVacSignal(new VacConnectedSignal2("VAC_CONNECTED" /* VAC_Connected */, null));
    dispatch({
      type: "CLEAR_ERRORS" /* CLEAR_ERRORS */,
      payload: {
        source: ORCHESTRATOR
      }
    });
  };
};
var websocketDisconnectedCallback = (publishVacSignal, dispatch, errorStrings) => {
  return () => {
    publishVacSignal(new VacDisconnectedSignal2("VAC_DISCONNECTED" /* VAC_Disconnected */, null));
    dispatch({
      type: "SET_ORCHESTRATOR_ERROR" /* SET_ORCHESTRATOR_ERROR */,
      payload: {
        errorName: "connectionLost",
        errorStrings
      }
    });
    dispatch({
      type: "CLEAR_ALL_TYPING_INDICATORS" /* CLEAR_ALL_TYPING_INDICATORS */,
      payload: {}
    });
    dispatch({
      type: "SET_LAST_WEBSOCKET_DISCONNECT_TIME" /* SET_LAST_WEBSOCKET_DISCONNECT_TIME */,
      payload: Date.now()
    });
  };
};

// src/components/websocket-connector/websocket-connector.tsx
var supportedTriggers = ["message", "typingIndicator", "conversation", "event", "participant"];
var WebSocketConnector = ({
  refetch
}) => {
  var _a, _b;
  const [state, dispatch] = useProvidedState3();
  const {
    publish
  } = useSignalPublisher2();
  const {
    context
  } = useBexApi3();
  const {
    ablyRealtime
  } = useWebsocket();
  const {
    eventJoined,
    eventLeft,
    typeHumanAgent,
    typeTraveler
  } = use_prefetch_localization_content_default(LocalizationContentKeys.PARTICIPANT);
  const messageContent = use_prefetch_localization_content_default(LocalizationContentKeys.MESSAGE);
  const ablyConnectRef = React6.useRef(null);
  const errorStrings = (_b = (_a = state.errorsState) == null ? void 0 : _a.orchestrator) == null ? void 0 : _b.offlineLocalizedErrorStrings;
  const isTodaySeperatorAdded = React6.useRef(false);
  const logEvents = useLoggerEvents(LogEvents);
  React6.useEffect(() => {
    if (!ablyConnectRef.current) {
      ablyConnectRef.current = ablyRealtime;
    }
  }, [ablyRealtime]);
  const addTodaysSeparatorMessage = conversationId => {
    if (!Object.keys(state.messagesState.messagesByConversationId).length && !isTodaySeperatorAdded.current) {
      isTodaySeperatorAdded.current = true;
      const timeStamp = startOfDayUTC(/* @__PURE__ */new Date()).toISOString();
      dispatch({
        type: "ADD_SEPERATOR_MESSAGE" /* ADD_SEPERATOR_MESSAGE */,
        payload: {
          primary: new Date(Date.now()).toUTCString(),
          conversationId,
          timeStamp
        }
      });
    }
  };
  const handleMessageReceived = async message => {
    var _a2, _b2, _c, _d, _e, _f;
    let event;
    try {
      event = await parseWebsocketMessage(message.data);
    } catch (error) {
      logEvents.WEBSOCKET_MESSAGE_PARSING_ERROR.log({
        subscriptionId: state.orchestratorState.vacConfig.websocket.subscriptionId,
        error: error.message
      });
    }
    if (!event || !supportedTriggers.includes(event.trigger)) {
      return;
    }
    if (event.trigger === "typingIndicator") {
      if (((_a2 = event.typingIndicator) == null ? void 0 : _a2.start) === true) {
        const parsedParams = getTypingIndicatorEvent(event.typingIndicator, event.conversationId);
        dispatch({
          type: "SET_TYPING_INDICATOR" /* SET_TYPING_INDICATOR */,
          payload: {
            typingIndicator: parsedParams
          }
        });
      }
    } else if (event.trigger === "conversation") {
      if ((_b2 = event.conversation) == null ? void 0 : _b2.conversationClosed) {
        dispatch({
          type: "UPDATE_CONVERSATION" /* UPDATE_CONVERSATION */,
          payload: {
            conversationId: event.conversationId,
            state: "CLOSED" /* CLOSED */
          }
        });
      }
    } else if (event.trigger === "participant") {
      const {
        participant,
        conversationId,
        triggerTime
      } = event;
      const {
        leaveContent,
        joinContent,
        updateContent,
        participantType,
        participantId
      } = participant || {};
      let eventName;
      if (joinContent) {
        eventName = "Participant.ParticipantJoined" /* ParticipantJoined */;
      } else if (leaveContent) {
        eventName = "Participant.ParticipantLeft" /* ParticipantLeft */;
      } else if (updateContent) {
        eventName = "Participant.ParticipantUpdate" /* ParticipantUpdate */;
      }
      if (!participant || !eventName) {
        return;
      }
      if (eventName === "Participant.ParticipantJoined" /* ParticipantJoined */) {
        const participantPayload = parseWebsocketParticipants(participant);
        dispatch({
          type: "SET_PARTICIPANTS" /* SET_PARTICIPANTS */,
          payload: {
            conversationId,
            participants: participantPayload
          }
        });
      } else if (eventName === "Participant.ParticipantLeft" /* ParticipantLeft */) {
        dispatch({
          type: "MARK_PARTICIPANT_LEFT" /* MARK_PARTICIPANT_LEFT */,
          payload: {
            participantId
          }
        });
      }
      const defaultParticipantName = participantType === "HumanAgent" /* HumanAgent */ ? typeHumanAgent : typeTraveler;
      if (eventName === "Participant.ParticipantJoined" /* ParticipantJoined */ || eventName === "Participant.ParticipantLeft" /* ParticipantLeft */) {
        const localizationStrings = {
          ["Participant.ParticipantJoined" /* ParticipantJoined */]: eventJoined,
          ["Participant.ParticipantLeft" /* ParticipantLeft */]: eventLeft
        };
        const eventTypeString = localizationStrings[eventName];
        addTodaysSeparatorMessage(conversationId);
        const conversationParticipant = eventName === "Participant.ParticipantLeft" /* ParticipantLeft */ ? state.participantState.participants[participantId] : participant;
        const participantDisplayName = getPartcipantDisplayName(conversationParticipant);
        publish(new VacParticipantEventsSignal("VAC_PARTICIPANT_EVENTS" /* VAC_Participant_Event */, {
          markerString: localizeTemplateLiteral(eventTypeString, "participantDisplayName", participantDisplayName || defaultParticipantName),
          conversationId,
          timeStamp: triggerTime,
          participantType,
          participantId,
          eventName
        }));
      } else if (eventName === "Participant.ParticipantUpdate" /* ParticipantUpdate */) {
        const participantDisplayName = getPartcipantDisplayName(participant);
        const participantFromState = state.participantState.participants[participant.participantId];
        if (!participantDisplayName || !participantFromState) {
          return;
        }
        const [participantPayload] = parseWebsocketParticipants(participant);
        dispatch({
          type: "SET_PARTICIPANTS" /* SET_PARTICIPANTS */,
          payload: {
            conversationId,
            participants: [{
              ...participantFromState,
              user: participantPayload.user
            }]
          }
        });
        dispatch({
          type: "UPDATE_PARTICIPANT_MARKERS" /* UPDATE_PARTICIPANT_MARKERS */,
          payload: {
            conversationId: event.conversationId,
            participantId,
            primary: localizeTemplateLiteral(eventJoined, "participantDisplayName", participantDisplayName)
          }
        });
      }
    } else if (event.trigger === "event") {
      if (((_c = event.event) == null ? void 0 : _c.eventType) === "MESSAGE_RECEIVED") {
        const {
          messageIdSet
        } = state.messagesState.messagesByConversationId[event.conversationId] || {};
        const {
          message: receivedMessage
        } = ((_d = event.event) == null ? void 0 : _d.messageReceived) || {};
        const parsedMessage = getFormattedMessage(receivedMessage);
        if (!(messageIdSet == null ? void 0 : messageIdSet.has(receivedMessage == null ? void 0 : receivedMessage.referenceMessageId))) {
          const outboundMessage = getOutboundMessage(parsedMessage);
          dispatch({
            type: "ADD_MESSAGE" /* ADD_MESSAGE */,
            payload: {
              conversationId: event.conversationId,
              message: outboundMessage,
              messageContent
            }
          });
        }
        if ((_e = receivedMessage == null ? void 0 : receivedMessage.data) == null ? void 0 : _e.flags) {
          const {
            disableUserInput = false
          } = receivedMessage.data.flags;
          dispatch({
            type: "SET_FOOTER_STATE" /* SET_FOOTER_STATE */,
            payload: {
              conversationId: event.conversationId,
              chatFooterState: {
                chatInputStatus: disableUserInput
              }
            }
          });
        }
      }
    } else if (event.message) {
      const parsedMessage = getFormattedMessage(event.message);
      if ((parsedMessage == null ? void 0 : parsedMessage.__typename) === "VirtualAgentControlInboundTextMessage" && parsedMessage.metadata.isActivityIndicatorMessage) {
        const activityIndicatorParams = getActivityIndicatorParams(parsedMessage, event.conversationId);
        dispatch({
          type: "SET_TYPING_INDICATOR" /* SET_TYPING_INDICATOR */,
          payload: {
            typingIndicator: activityIndicatorParams
          }
        });
      } else {
        const chatFooterState = getFormattedChatFooterState(event.message);
        const clearIndicator = getClearIndicatorParams(event.message, event.conversationId);
        dispatch({
          type: "SET_TYPING_INDICATOR" /* SET_TYPING_INDICATOR */,
          payload: {
            typingIndicator: clearIndicator
          }
        });
        if (chatFooterState) {
          dispatch({
            type: "SET_FOOTER_STATE" /* SET_FOOTER_STATE */,
            payload: {
              conversationId: event.conversationId,
              chatFooterState
            }
          });
        }
        const {
          quickReplies,
          analytics: quickReplyAnalytics
        } = getQuickReplies(event.message);
        if (quickReplies) {
          dispatch({
            type: "SET_QUICK_REPLIES" /* SET_QUICK_REPLIES */,
            payload: {
              conversationId: event.conversationId,
              message: quickReplies,
              messageId: event.message.messageId,
              analytics: quickReplyAnalytics
            }
          });
        }
        if (parsedMessage) {
          dispatch({
            type: "SET_UNREAD_MESSAGE_COUNT" /* SET_UNREAD_MESSAGE_COUNT */,
            payload: {
              conversationId: event.conversationId
            }
          });
          dispatch({
            type: "ADD_MESSAGE" /* ADD_MESSAGE */,
            payload: {
              conversationId: event.conversationId,
              message: parsedMessage,
              messageContent
            }
          });
          if (isInboundMessage(parsedMessage)) {
            publish(new VacNotificationSoundSignal("VAC_NOTIFICATION_SOUND" /* VAC_NotificationSound */, null));
            dispatch({
              type: "SET_ANNOUNCE_MESSAGE_ID" /* SET_ANNOUNCE_MESSAGE_ID */,
              payload: {
                conversationId: event.conversationId,
                messageId: (_f = event.message) == null ? void 0 : _f.messageId
              }
            });
          }
          publish(new VacConversationReadSignal("VAC_CONVERSATION_READ" /* VAC_ConversationRead */, {
            conversationId: event.conversationId
          }));
        }
      }
    }
  };
  useSignalSubscriber3("VAC_CHAT_WINDOW_VISIBLE" /* VAC_ChatWindowVisible */, VacChatWindowVisibleSignal, {
    onSuccess: () => {
      if (ablyConnectRef.current) {
        return;
      }
      const {
        token,
        subscriptionId,
        clientId
      } = state.orchestratorState.vacConfig.websocket;
      const variables = {
        context,
        clientId
      };
      const websocketEvent = {
        vacId: globalEventId,
        type: "VAC_CONNECT_WEBSOCKET" /* VAC_ConnectWebsocket */,
        payload: {
          client: {
            authCallback: refetchToken(dispatch, refetch, errorStrings, variables),
            clientId,
            token,
            environment: "expedia",
            fallbackHosts: ["expedia-a-fallback.ably-realtime.com", "expedia-b-fallback.ably-realtime.com", "expedia-c-fallback.ably-realtime.com", "expedia-d-fallback.ably-realtime.com", "expedia-e-fallback.ably-realtime.com"]
          },
          subscriptionId,
          lifeCycleCallbacks: {
            onConnected: websocketConnectedCallback(publish, dispatch),
            onDisconnected: websocketDisconnectedCallback(publish, dispatch, errorStrings)
          },
          lifeCycleCallbackDependencies: []
        }
      };
      publish(new VacConnectWebsocketSignal2("VAC_CONNECT_WEBSOCKET" /* VAC_ConnectWebsocket */, websocketEvent.payload));
    }
  });
  useSignalSubscriber3("VAC_CONNECTED" /* VAC_Connected */, VacConnectedSignal3, {
    onSuccess: () => {
      dispatch({
        type: "SET_INIT_STATUS" /* SET_INIT_STATUS */,
        payload: "DONE" /* DONE */
      });
    }
  });
  useSignalSubscriber3("VAC_CHAT_MESSAGE_RECEIVED" /* VAC_ChatMsgReceived */, VacChatMessageReceivedSignal2, {
    onSuccess(signal) {
      const {
        payload: {
          message
        }
      } = signal;
      handleMessageReceived(message);
    }
  });
  useSignalSubscriber3("VAC_RECONNECT" /* VAC_Reconnect */, VacReconnectSignal, {
    onSuccess() {
      var _a2;
      (_a2 = ablyConnectRef.current) == null ? void 0 : _a2.connect();
    }
  });
  return null;
};
WebSocketConnector.displayName = "WebSocketConnector";
var websocket_connector_default = WebSocketConnector;

// src/components/vac-orchestrator/vac-orchestrator.tsx
var __Query = gql3`
  query VacOrchestratorQuery($context: ContextInput!, $clientId: String) {
    virtualAgentControlConfiguration(context: $context, clientId: $clientId) {
      authToken
      webSocket {
        ablyConfig {
          clientId
          token
        }
        subscriptionId
      }
      localizationContent {
        id
        attachment {
          downloadError
          downloadProcessing
          downloadSuccess
          uploadError
          uploadFileSizeError
          uploadProcessing
          uploadSuccess
          outboundAttachmentAccessibilityLabelTemplate
        }
        dialog {
          closeWebviewWindow
        }
        activityIndicator {
          activityIndicatorMessage
          humanTyping
          multipleParticipantsTyping
          virtualAgentTyping
          typingIndicatorAccessibilityAnnouncementTemplate
        }
        error {
          havingTroubleLoading
          lostConnection
          refreshOrReconnect
          refreshOrTryLater
          refreshToContinue
          sessionExpired
          somethingWentWrong
          tryToReconnect
          removeExcessCharacters
          removeSingleExcessCharacter
        }
        loading {
          downloadChatTranscript
        }
        message {
          separatorToday
          separatorYesterday
          statusNotDelivered
          statusSending
          statusRetry
          statusDelivered
          dcfUnavailableLine1
          dcfUnavailableLine2
          timestampAccessibilityLabelTemplate
          messagesLoadedNotificationAnnouncement
        }
        participant {
          typeHumanAgent
          typeSupplier
          typeTraveler
          typeVirtualAgent
          eventJoined
          eventLeft
          outboundMessageContextAccessibilityLabel
        }
      }
    }
  }
`;
function getSuccessHandler(dispatch) {
  return result => {
    var _a, _b;
    const {
      authToken,
      webSocket,
      localizationContent
    } = (result == null ? void 0 : result.virtualAgentControlConfiguration) || {};
    dispatch({
      type: "SET_OFFLINE_ERROR_STRINGS" /* SET_OFFLINE_ERROR_STRINGS */,
      payload: {
        offlineLocalizedErrorStrings: Omit2(localizationContent == null ? void 0 : localizationContent.error, ["__typename"])
      }
    });
    dispatch({
      type: "SET_VAC_CONFIGURATION" /* SET_VAC_CONFIGURATION */,
      payload: {
        authToken,
        websocket: {
          subscriptionId: webSocket == null ? void 0 : webSocket.subscriptionId,
          token: (_a = webSocket == null ? void 0 : webSocket.ablyConfig) == null ? void 0 : _a.token,
          clientId: (_b = webSocket == null ? void 0 : webSocket.ablyConfig) == null ? void 0 : _b.clientId
        }
      }
    });
    dispatch({
      type: "SET_INIT_STATUS" /* SET_INIT_STATUS */,
      payload: "PAUSED" /* PAUSED */
    });
  };
}
var VacOrchestrator = ({
  children,
  skipSsr
}) => {
  var _a, _b, _c;
  const {
    context
  } = useBexApi4();
  const [state, dispatch] = useProvidedState4();
  const {
    publish
  } = useSignalPublisher3();
  const {
    startTimer
  } = useTimers();
  React7.useEffect(() => startTimer(TIMERS.INIT), []);
  const variables = {
    context,
    clientId: null
  };
  const {
    error,
    loading,
    refetch
  } = useQuery(__Query, {
    context: {
      shouldLogPerformance: true
    },
    variables,
    ssr: !skipSsr,
    onCompleted: result => {
      publish(new VacOrchestratorQuerySignal("VAC_ORCHESTRATOR_QUERY_SIGNAL" /* VAC_OrchestatorQuery */, {
        success: true
      }));
      getSuccessHandler(dispatch)(result);
    },
    onError: apiError => {
      publish(new VacOrchestratorQuerySignal("VAC_ORCHESTRATOR_QUERY_SIGNAL" /* VAC_OrchestatorQuery */, {
        success: false
      }));
      getErrorHandler(dispatch, publish)(apiError);
    }
  });
  if (loading || error) {
    return null;
  }
  return /* @__PURE__ */React7.createElement(React7.Fragment, null, ((_c = (_b = (_a = state.orchestratorState) == null ? void 0 : _a.vacConfig) == null ? void 0 : _b.websocket) == null ? void 0 : _c.token) && /* @__PURE__ */React7.createElement(websocket_connector_default, {
    refetch
  }), /* @__PURE__ */React7.createElement(heartbeat_manager_default, null), children);
};
VacOrchestrator.displayName = "sharedui-VacOrchestrator";

// src/components/index.ts
var VacOrchestrator2 = withVacStateProvider(withTimers(withWebsocket(VacOrchestrator)));
export { VacOrchestrator2 as VacOrchestrator };
/* istanbul ignore file -- @preserve */
//# sourceMappingURL=index.js.map